// =============================================================================
// CHAT-WIDGET.JS — Custom Socket.IO chat system
// =============================================================================
// Self-contained IIFE providing real-time chat for the live auction page.
// Features: message sending/receiving, pinned messages, emoji reactions,
// admin moderation (delete, timeout, ban), and screen name display.
// Replaced the former Pigeonhole third-party chat integration.

(function() {
  // Configuration
  var HEROKU_SERVER_URL = MHV.SERVER_URL;

  // Delegated click handler for data-act buttons built into HTML strings
  // (retires interpolated inline on* handlers — SEC-61/CSP). Registered ONCE.
  var _chatDelegatedClickBound = false;
  function bindChatDelegatedClicks() {
    if (_chatDelegatedClickBound) return;
    _chatDelegatedClickBound = true;
    document.addEventListener('click', function(e) {
      var el = e.target.closest ? e.target.closest('[data-act]') : null;
      if (!el) return;
      switch (el.getAttribute('data-act')) {
        case 'toggle-description':
          if (window.toggleDescription) window.toggleDescription(el.getAttribute('data-p0'), e);
          break;
      }
    });
  }

  // Wait for Socket.IO library to be available
  function waitForSocketIO(callback) {
    if (typeof io !== 'undefined') {
      callback();
    } else {
      setTimeout(function() { waitForSocketIO(callback); }, 100);
    }
  }


  // Sanitize a lot description (admin-entered product HTML that we broadcast to ALL
  // viewers) into a safe formatting subset. The old regex blacklist (strip <script>,
  // <style>, on*="…") missed single-quoted/unquoted handlers, javascript:/data: URLs,
  // <img onerror>, <iframe>, etc. This parses in an INERT document (DOMParser — no
  // scripts run, no images load), unwraps anything outside an allow-list of formatting
  // tags, and strips every event-handler / dangerous attribute (SEC-61).
  var LOT_DESC_ALLOWED = { P:1,BR:1,B:1,STRONG:1,I:1,EM:1,U:1,SPAN:1,DIV:1,UL:1,OL:1,LI:1,HR:1,
    H1:1,H2:1,H3:1,H4:1,H5:1,H6:1,A:1,BLOCKQUOTE:1,SMALL:1,SUP:1,SUB:1 };
  function sanitizeLotDescription(html) {
    var raw = String(html == null ? '' : html);
    if (!raw) return '';
    var doc;
    try { doc = new DOMParser().parseFromString(raw, 'text/html'); } catch (e) { return ''; }
    if (!doc || !doc.body) return '';
    var all = doc.body.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (!LOT_DESC_ALLOWED[el.tagName]) {
        // Unwrap: replace the disallowed element with its plain text.
        if (el.parentNode) el.parentNode.replaceChild(doc.createTextNode(el.textContent || ''), el);
        continue;
      }
      for (var a = el.attributes.length - 1; a >= 0; a--) {
        var name = el.attributes[a].name;
        var lname = name.toLowerCase();
        var val = el.attributes[a].value || '';
        if (lname.indexOf('on') === 0 || lname === 'style') { el.removeAttribute(name); continue; }
        if ((lname === 'href' || lname === 'src' || lname === 'xlink:href') && /^\s*(javascript|data|vbscript):/i.test(val)) {
          el.removeAttribute(name);
        }
      }
    }
    return doc.body.innerHTML;
  }

  // Format dimensions text: bold measurements, italic modifiers, line spacing
  function formatDimensionsHtml(text) {
    if (!text) return '';
    var lines = text.split('\n');
    var parts = [];
    for (var i = 0; i < lines.length; i++) {
      var line = MHV.escapeHtml(lines[i]);
      if (i === 0) {
        // First line is the actual measurements — bold
        parts.push('<strong>' + line + '</strong>');
      } else {
        // Subsequent lines are modifiers/notes — italic, slightly smaller
        parts.push('<em class="dims-modifier">' + line + '</em>');
      }
    }
    return parts.join('<br>');
  }

  // Format timestamp (includes seconds, uses user's local timezone)
  function formatTime(timestamp) {
    var date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  // Get initials from name
  function getInitials(name) {
    if (!name) return '?';
    var parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  // Create message HTML (isAdmin passed from outer scope)
  function createMessageHtml(data, isOwnMessage, isAdminViewer, viewerBidderNumber) {
    // Resolve display name (privacy-aware; admins/employees always see the real
    // name). Absentee messages encode bidder info in firstName ("BIDDER:..."), so
    // don't treat that as a real name here.
    var isBidderEncoded = data.firstName && data.firstName.indexOf('BIDDER:') === 0;
    var disp = MHV.chatDisplayName({
      screenName: data.screenName,
      firstName: isBidderEncoded ? '' : data.firstName,
      lastName: data.lastName,
      bidderNumber: data.bidderNumber,
      hideName: data.hideName,
      isAdminView: isAdminViewer
    });

    var avatarHtml = '';
    if (data.profileImage) {
      avatarHtml = '<img src="' + MHV.escapeAttr(data.profileImage) + '" alt="" class="custom-chat-avatar" onerror="this.style.display=\'none\';this.nextElementSibling.style.display=\'flex\';">' +
                   '<div class="custom-chat-avatar-placeholder" style="display:none;">' + MHV.escapeHtml(getInitials(disp.primary)) + '</div>';
    } else {
      avatarHtml = '<div class="custom-chat-avatar-placeholder">' + MHV.escapeHtml(getInitials(disp.primary)) + '</div>';
    }

    // Build first line: display name, badge, bidder number. For the bidder
    // fallback (no screen name + hidden), show just "Bidder" — the number pill
    // below renders the actual number.
    var nameText = disp.bidderFallback ? 'Bidder' : disp.primary;
    var usernameHtml = '<span class="custom-chat-name">' + MHV.escapeHtml(nameText) + '</span>';

    if (data.badge) {
      usernameHtml += '<span class="custom-chat-badge">' + MHV.escapeHtml(data.badge) + '</span>';
    }

    if (data.bidderNumber) {
      usernameHtml += '<span class="custom-chat-bidder-number">' + MHV.escapeHtml(data.bidderNumber) + '</span>';
    }

    // Add ban button for admins (not on own messages)
    var banBtnHtml = '';
    if (isAdminViewer && !isOwnMessage && data.customerId) {
      banBtnHtml = '<button class="custom-chat-ban-btn" data-customer-id="' + MHV.escapeAttr(data.customerId) + '" data-screen-name="' + MHV.escapeAttr(disp.primary) + '" title="Ban this user">🚫</button>';
    }

    // Add delete button for admins
    var deleteBtnHtml = '';
    if (isAdminViewer && data.id) {
      deleteBtnHtml = '<button class="custom-chat-message-delete" data-message-id="' + MHV.escapeAttr(data.id) + '" title="Delete message">✕</button>';
    }
    
    // Add pin button for admins
    var pinBtnHtml = '';
    if (isAdminViewer && data.id) {
      pinBtnHtml = '<button class="custom-chat-pin-btn" data-message-id="' + MHV.escapeAttr(data.id) + '" data-message-type="message" data-screen-name="' + MHV.escapeAttr(disp.primary) + '" data-message-text="' + MHV.escapeAttr(data.message) + '" title="Pin message">📌</button>';
    }

    // Build second line: real name "(First L.)" shown UNDER a screen name when
    // revealed. chatDisplayName already applied the hide-name preference + the
    // admin-always-reveal rule, so we just render whatever it returns.
    var metaLineHtml = '';
    if (disp.secondary) {
      metaLineHtml += '<span class="custom-chat-firstname">' + MHV.escapeHtml(disp.secondary) + '</span>';
    }
    metaLineHtml += '<span class="custom-chat-timestamp">' + formatTime(data.timestamp) + '</span>';

    // Build reactions display
    var reactionsHtml = buildReactionsHtml(data.reactions || {}, data.id);

    // Reaction button and picker (only for messages with IDs)
    var reactionBtnHtml = '';
    if (data.id) {
      reactionBtnHtml = '<div class="custom-chat-reaction-container">' +
        '<button class="custom-chat-reaction-btn" data-message-id="' + MHV.escapeAttr(data.id) + '" title="React">👍</button>' +
        '<div class="custom-chat-reaction-picker" data-message-id="' + MHV.escapeAttr(data.id) + '">' +
          '<button data-emoji="👍">👍</button>' +
          '<button data-emoji="❤️">❤️</button>' +
          '<button data-emoji="💔">💔</button>' +
          '<button data-emoji="🤩">🤩</button>' +
          '<button data-emoji="😍">😍</button>' +
          '<button data-emoji="😭">😭</button>' +
          '<button data-emoji="😮">😮</button>' +
          '<button data-emoji="🤬">🤬</button>' +
          '<button data-emoji="😈">😈</button>' +
          '<button data-emoji="☠️">☠️</button>' +
          '<button data-emoji="👻">👻</button>' +
          '<button data-emoji="👽">👽</button>' +
          '<button data-emoji="🎃">🎃</button>' +
        '</div>' +
      '</div>';
    }

    var messageClasses = 'custom-chat-message';
    if (isOwnMessage) messageClasses += ' own-message';
    // The sender key rides on the element so a key that arrives AFTER the render
    // (sender-key / chat-state) can still mark this message as mine — remarkMine().
    var senderKeyAttr = data.senderKey ? ' data-sender-key="' + MHV.escapeAttr(String(data.senderKey)) + '"' : '';
    if (data.isAbsentee) messageClasses += ' absentee-message';
    
    // Build absentee bidder info line if present
    // Server now passes through absenteeBidder* fields, but also check encoded firstName as fallback
    var absenteeBidderHtml = '';
    if (data.isAbsentee) {
      var absenteeBidderName = data.absenteeBidderName || '';
      var absenteeBidderBadge = data.absenteeBidderBadge || '';
      var absenteeBidderNumber = data.absenteeBidderNumber || '';
      
      // Fallback: parse from encoded firstName if fields not present
      if (!absenteeBidderName && data.firstName && data.firstName.indexOf('BIDDER:') === 0) {
        var bidderInfoStr = data.firstName.substring(7);
        var bidderParts = bidderInfoStr.split('::');
        absenteeBidderName = bidderParts[0] || '';
        absenteeBidderBadge = bidderParts[1] || '';
        absenteeBidderNumber = bidderParts[2] || '';
      }
      
      if (absenteeBidderName) {
        // Check if this absentee bid is from the current viewer
        var isViewersBid = viewerBidderNumber && absenteeBidderNumber &&
                           viewerBidderNumber.toString() === absenteeBidderNumber.toString();

        absenteeBidderHtml = '<div class="absentee-bidder-info">' +
          '<span class="absentee-bidder-name">' + MHV.escapeHtml(absenteeBidderName) + '</span>';
        if (absenteeBidderBadge) {
          absenteeBidderHtml += '<span class="absentee-bidder-badge">' + MHV.escapeHtml(absenteeBidderBadge) + '</span>';
        }
        if (absenteeBidderNumber) {
          absenteeBidderHtml += '<span class="absentee-bidder-number">' + MHV.escapeHtml(absenteeBidderNumber) + '</span>';
        }
        // Show (FIRSTNAME) for admins only — matches normal chat style
        var absenteeBidderFirstName = data.absenteeBidderFirstName || '';
        if (absenteeBidderFirstName && isAdminViewer) {
          absenteeBidderHtml += '<span class="custom-chat-firstname">(' + MHV.escapeHtml(absenteeBidderFirstName).toUpperCase() + ')</span>';
        }
        // Show YOU badge if this is the viewer's bid
        if (isViewersBid) {
          absenteeBidderHtml += '<span class="absentee-you-badge">YOU</span>';
        }
        absenteeBidderHtml += '</div>';
      }
    }
    
    // For absentee messages with bidder info, strip the suffix from display (it's there for Google Sheets logging)
    var displayMessage = data.message;
    if (data.isAbsentee && absenteeBidderHtml) {
      // Remove suffix like " (BidderName #123)" or " (BidderName)"
      displayMessage = displayMessage.replace(/\s*\([^)]+#?\d*\)\s*$/, '');
    }
    
    // A profile can be opened by customer id (staff — they still receive it) or by
    // bidder number (everyone — Q27: "bidder number is always fine"). The public
    // payload no longer carries the id, so the bidder number is what opens the popup
    // for the room; a guest with neither has no popup, exactly as before.
    var profileAttrs = (data.customerId || data.bidderNumber) && !data.isAbsentee
      ? (data.customerId ? ' data-customer-id="' + MHV.escapeAttr(data.customerId) + '"' : '') +
        ' data-profile-key="1"' +
        ' data-screen-name="' + MHV.escapeAttr(disp.primary || '') + '"' +
        ' data-profile-image="' + MHV.escapeAttr(data.profileImage || '') + '"' +
        ' data-badge="' + MHV.escapeAttr(data.badge || '') + '"' +
        ' data-bidder-number="' + MHV.escapeAttr(data.bidderNumber || '') + '"'
      : '';

    return '<div class="' + messageClasses + '" data-message-id="' + MHV.escapeAttr(data.id || '') + '"' + profileAttrs + senderKeyAttr + '>' +
             '<div class="custom-chat-admin-btns">' + pinBtnHtml + deleteBtnHtml + '</div>' +
             avatarHtml +
             '<div class="custom-chat-message-content">' +
               '<div class="custom-chat-message-header">' +
                 '<span class="custom-chat-username">' + usernameHtml + banBtnHtml + '</span>' +
                 absenteeBidderHtml +
                 '<div class="custom-chat-meta-line">' + metaLineHtml + '</div>' +
               '</div>' +
               '<div class="custom-chat-text">' + renderChatMessageContent(displayMessage) + '</div>' +
               reactionsHtml +
             '</div>' +
             reactionBtnHtml +
           '</div>';
  }

  // Build HTML for reactions display
  function buildReactionsHtml(reactions, messageId) {
    if (!reactions || Object.keys(reactions).length === 0) {
      return '<div class="custom-chat-reactions" data-message-id="' + MHV.escapeAttr(messageId || '') + '"></div>';
    }

    var html = '<div class="custom-chat-reactions" data-message-id="' + MHV.escapeAttr(messageId || '') + '">';
    for (var emoji in reactions) {
      if (reactions.hasOwnProperty(emoji) && reactions[emoji].length > 0) {
        html += '<span class="custom-chat-reaction-badge" data-emoji="' + MHV.escapeAttr(emoji) + '" title="' + MHV.escapeAttr(reactions[emoji].join(', ')) + '">' +
                  '<span class="emoji">' + MHV.escapeHtml(emoji) + '</span>' +
                  '<span class="count">' + reactions[emoji].length + '</span>' +
                '</span>';
      }
    }
    html += '</div>';
    return html;
  }

  // Initialize the custom chat widget
  function initCustomChat(container) {
    bindChatDelegatedClicks();
    var chatContainer = container.querySelector('#custom-chat-container') || container.querySelector('.custom-chat-container');
    if (!chatContainer) {
      console.warn('Custom Chat: Container not found');
      return;
    }

    // Get container dimensions
    var rect = container.getBoundingClientRect();
    var width = rect.width;
    var height = rect.height;

    // Only initialize if we have valid dimensions.
    //
    // ⚠️ A WIDGET THAT IS NOT ON SCREEN HAS NO DIMENSIONS, AND NEVER WILL.
    // This retried every 100ms for ever, warning each time. On a host layout with
    // chat left out of the columns that is an infinite loop: thousands of console
    // lines, a timer that never sleeps, and — worse — a console so flooded that
    // real errors scroll past unread. That is how it hid.
    //
    // So: retry only while the widget is genuinely still LAYING OUT (present, but
    // not yet measured), and give that a hard ceiling. If it is hidden or absent,
    // stop and wait to be told it appeared — ResizeObserver fires the moment it
    // gets a size, so nothing is lost by not spinning.
    if (width === 0 || height === 0) {
      var tries = (container.__chatInitTries || 0) + 1;
      container.__chatInitTries = tries;
      var isHidden = !container.offsetParent && getComputedStyle(container).position !== 'fixed';
      var MAX_TRIES = 30;   // ~3s of genuine layout settling

      if (!isHidden && tries <= MAX_TRIES) {
        setTimeout(function() { initCustomChat(container); }, 100);
        return;
      }

      // Stand down. Watch instead — this costs nothing until the widget is shown.
      if (!container.__chatSizeWatcher && typeof ResizeObserver === 'function') {
        container.__chatSizeWatcher = new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var r = entries[i].contentRect;
            if (r.width > 0 && r.height > 0) {
              container.__chatSizeWatcher.disconnect();
              container.__chatSizeWatcher = null;
              container.__chatInitTries = 0;
              initCustomChat(container);
              return;
            }
          }
        });
        container.__chatSizeWatcher.observe(container);
        console.log('Custom Chat: not on screen — waiting until it is shown (no more retries).');
      }
      return;
    }
    container.__chatInitTries = 0;

    // Get customer data from container data attributes
    var customerId = chatContainer.dataset.customerId || '';
    // This socket's own sender key (Q27) — set from chat-state, compared against
    // every message's senderKey to style "mine". Empty for a guest.
    var mySenderKey = '';
    // Messages and BIN sales rendered BEFORE this socket learned its own key: mark
    // the ones that are mine now the key is known. Idempotent.
    function remarkMine() {
      if (!mySenderKey) return;
      try {
        var mine = document.querySelectorAll('.custom-chat-message[data-sender-key="' + mySenderKey + '"]');
        for (var i = 0; i < mine.length; i++) mine[i].classList.add('own-message');
        var bought = document.querySelectorAll('.custom-chat-bin-announcement.sold[data-buyer-key="' + mySenderKey + '"]');
        for (var j = 0; j < bought.length; j++) {
          bought[j].classList.remove('sold-other');
          bought[j].classList.add('sold-to-me');
          var info = bought[j].querySelector('.bin-sold-info');
          if (info) info.textContent = 'You purchased this item!';
        }
      } catch (e) {}
    }
    // A logged-in customer with no screen name must resolve to "First L." (done by
    // MHV.chatDisplayName using firstName/lastName), so DON'T fabricate "Guest" for
    // them — that string would be sent as their screen name and shown verbatim.
    // Only a true (not-logged-in) guest falls back to "Guest".
    var customerName = chatContainer.dataset.customerName || (customerId ? '' : 'Guest');
    var customerFirstName = chatContainer.dataset.customerFirstName || '';
    var customerLastName = chatContainer.dataset.customerLastName || '';
    var customerBadge = chatContainer.dataset.customerBadge || '';
    var customerBidderNumber = chatContainer.dataset.customerBidderNumber || '';
    var customerImage = chatContainer.dataset.customerImage || '';
    var isLoggedIn = !!customerId;
    var isAdmin = chatContainer.dataset.isAdmin === 'true';
    var isBanned = chatContainer.dataset.isBanned === 'true';
    
    // Get device/browser information for logging
    var deviceInfo = MHV.getDeviceInfo();

    // Get DOM elements
    var messagesContainer = chatContainer.querySelector('.custom-chat-messages');
    var loadingEl = chatContainer.querySelector('.custom-chat-loading');
    var inputEl = chatContainer.querySelector('#custom-chat-input');
    var sendBtn = chatContainer.querySelector('#custom-chat-send');
    var newMessagesIndicator = chatContainer.querySelector('#new-messages-indicator');

    // --- Connection status banner ---------------------------------------------
    // A visible bar so users KNOW when chat drops (and that it's auto-recovering),
    // instead of silently sitting on a dead connection. Also locks the composer
    // while disconnected so messages aren't typed into the void.
    var statusBar = document.createElement('div');
    statusBar.className = 'custom-chat-status';
    statusBar.style.cssText = 'display:none;padding:7px 10px;font-size:12px;font-weight:600;text-align:center;background:#8a6d00;color:#fff;letter-spacing:.2px;';
    statusBar.textContent = '⚠️ Reconnecting to chat…';
    if (messagesContainer && messagesContainer.parentNode) {
      messagesContainer.parentNode.insertBefore(statusBar, messagesContainer);
    }
    var chatIsConnected = true;
    function setChatConnected(connected) {
      if (connected === chatIsConnected && statusBar.style.display === (connected ? 'none' : 'block')) return;
      chatIsConnected = connected;
      statusBar.style.display = connected ? 'none' : 'block';
      if (!connected) statusBar.textContent = '⚠️ Reconnecting to chat…';
      // Only manage the composer for users who can actually chat (logged in).
      if (!isLoggedIn) return;
      if (inputEl) {
        if (!connected) {
          if (inputEl.dataset.phSaved === undefined) inputEl.dataset.phSaved = inputEl.placeholder || '';
          inputEl.placeholder = 'Reconnecting to chat…';
          inputEl.disabled = true;
        } else {
          if (inputEl.dataset.phSaved !== undefined) { inputEl.placeholder = inputEl.dataset.phSaved; delete inputEl.dataset.phSaved; }
          inputEl.disabled = false;
        }
      }
      if (sendBtn && !connected) sendBtn.disabled = true;
    }

    waitForSocketIO(function() {
      try {
        
        // Check if a socket already exists (e.g., from main-host.liquid)
        // If so, reuse it to avoid duplicate connections and double counting guests
        var socket;
        var isSharedSocket = false;
        
        if (window.socket && window.socket.connected) {
          // Reuse existing socket
          socket = window.socket;
          isSharedSocket = true;
          console.log('📡 Chat widget: Reusing existing socket connection');
        } else if (window.socket) {
          // Socket exists but not connected yet - use it and wait for connection
          socket = window.socket;
          isSharedSocket = true;
          console.log('📡 Chat widget: Reusing existing socket (waiting for connection)');
        } else {
          // No existing socket - create a new one (standalone mode).
          // reconnectionAttempts: Infinity — NEVER permanently give up. (A 5-try
          // cap meant a server/dyno restart could silently kill chat for good.)
          socket = io(HEROKU_SERVER_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000
          });
          console.log('📡 Chat widget: Created new socket connection (standalone mode)');
        }

        // Belt-and-suspenders: if the connection is down when the network comes
        // back or the tab is refocused (common on mobile after backgrounding),
        // nudge Socket.IO to reconnect immediately instead of waiting out backoff.
        function nudgeReconnect() {
          if (socket && !socket.connected) { try { socket.connect(); } catch (e) { /* noop */ } }
        }
        window.addEventListener('online', nudgeReconnect);
        document.addEventListener('visibilitychange', function() {
          if (document.visibilityState === 'visible') nudgeReconnect();
        });

        // Check if user is scrolled near the bottom (within 100px)
        function isNearBottom() {
          if (!messagesContainer) return true;
          var scrollTop = messagesContainer.scrollTop;
          var scrollHeight = messagesContainer.scrollHeight;
          var clientHeight = messagesContainer.clientHeight;
          return (scrollHeight - scrollTop - clientHeight) <= 100;
        }
        
        // Position the indicator DYNAMICALLY just above the bottom UI cluster (item 1):
        // the live bid bar when it's showing, else the chat input. Measured from the real
        // element positions so it's always correct regardless of the bar's height/presence
        // (no fixed offset). Recomputed on show + resize.
        function positionNewMessagesIndicator() {
          if (!newMessagesIndicator) return;
          var parent = newMessagesIndicator.offsetParent;
          if (!parent) return;
          var bar = chatContainer.querySelector('#live-bid-bar');
          var inputArea = chatContainer.querySelector('.custom-chat-input-area')
            || chatContainer.querySelector('.custom-chat-banned-notice');
          var visible = function (el) { return el && !el.hidden && el.offsetParent !== null; };
          // Top of the bottom cluster = the bar (sits above the input) when visible, else the input.
          var topEl = visible(bar) ? bar : (visible(inputArea) ? inputArea : null);
          if (!topEl) return;
          var bottom = (parent.getBoundingClientRect().bottom - topEl.getBoundingClientRect().top) + 8;
          if (bottom > 0) newMessagesIndicator.style.bottom = bottom + 'px';
        }

        // Show the new messages indicator
        function showNewMessagesIndicator() {
          if (newMessagesIndicator) {
            positionNewMessagesIndicator();
            newMessagesIndicator.classList.add('visible');
          }
        }
        window.addEventListener('resize', function () {
          if (newMessagesIndicator && newMessagesIndicator.classList.contains('visible')) positionNewMessagesIndicator();
        });
        // Hook so the live bid bar can re-anchor the indicator when it shows/hides/resizes.
        window.MHV_repositionChatIndicator = positionNewMessagesIndicator;
        
        // Hide the new messages indicator
        function hideNewMessagesIndicator() {
          if (newMessagesIndicator) {
            newMessagesIndicator.classList.remove('visible');
          }
        }

        // Auto-scroll to bottom (and hide indicator)
        function scrollToBottom() {
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
            hideNewMessagesIndicator();
          }
        }
        
        // Set up scroll listener to hide indicator when user scrolls to bottom
        if (messagesContainer) {
          messagesContainer.addEventListener('scroll', function() {
            if (isNearBottom()) {
              hideNewMessagesIndicator();
            }
          });
        }
        
        // Set up click handler for new messages indicator
        if (newMessagesIndicator) {
          newMessagesIndicator.addEventListener('click', function() {
            scrollToBottom();
          });
        }

        // Add message to chat
        function addMessage(data, isOwnMessage) {
          if (!messagesContainer) return;
          
          // Check scroll position BEFORE adding message (important for accurate detection)
          var wasNearBottom = isNearBottom();
          
          var messageHtml = createMessageHtml(data, isOwnMessage, isAdmin, customerBidderNumber);
          messagesContainer.insertAdjacentHTML('beforeend', messageHtml);
          
          // Auto-scroll if user was near bottom, otherwise show indicator
          if (wasNearBottom) {
            scrollToBottom();
          } else {
            showNewMessagesIndicator();
          }
          
          // Attach event handlers for admin buttons if admin
          if (isAdmin) {
            attachAdminButtonHandlers(messagesContainer.lastElementChild);
          }
        }
        
        // Attach click handlers to admin buttons on a message element
        function attachAdminButtonHandlers(messageEl) {
          if (!messageEl) return;
          
          // Delete button handler
          var deleteBtn = messageEl.querySelector('.custom-chat-message-delete');
          if (deleteBtn) {
            deleteBtn.addEventListener('click', function(e) {
              e.stopPropagation();
              var messageId = this.dataset.messageId;
              if (messageId) {
                socket.emit('admin-delete-message', { messageId: messageId });
              }
            });
          }
          
          // Pin button handler
          var pinBtn = messageEl.querySelector('.custom-chat-pin-btn');
          if (pinBtn) {
            pinBtn.addEventListener('click', function(e) {
              e.stopPropagation();
              var messageId = this.dataset.messageId;
              var messageType = this.dataset.messageType || 'message';
              var screenName = this.dataset.screenName || '';
              var messageText = this.dataset.messageText || '';
              
              // Capture original message HTML for pinned display (preserves original styling)
              var originalHtml = '';
              if (messageEl) {
                // Clone the message element and remove interactive/admin elements
                var clone = messageEl.cloneNode(true);
                var adminBtns = clone.querySelector('.custom-chat-admin-btns');
                if (adminBtns) adminBtns.remove();
                var reactionContainer = clone.querySelector('.custom-chat-reaction-container');
                if (reactionContainer) reactionContainer.remove();
                var banBtn = clone.querySelector('.custom-chat-ban-btn');
                if (banBtn) banBtn.remove();
                var reactions = clone.querySelector('.custom-chat-reactions');
                if (reactions) reactions.remove();
                // Use outerHTML to preserve the element's class and original styling
                originalHtml = clone.outerHTML;
              }
              
              if (messageId) {
                socket.emit('admin-pin-message', {
                  messageId: messageId,
                  type: messageType,
                  screenName: screenName,
                  text: messageText,
                  originalHtml: originalHtml,
                  timestamp: Date.now()
                });
              }
            });
          }
          
          // Ban button handler
          var banBtn = messageEl.querySelector('.custom-chat-ban-btn');
          if (banBtn) {
            banBtn.addEventListener('click', function(e) {
              e.stopPropagation();
              var targetCustomerId = this.dataset.customerId;
              var targetScreenName = this.dataset.screenName;
              if (targetCustomerId) {
                showBanConfirmation(targetCustomerId, targetScreenName);
              }
            });
          }
        }
        
        // Show ban confirmation modal
        function showBanConfirmation(targetCustomerId, targetScreenName) {
          var modal = document.createElement('div');
          modal.className = 'admin-confirm-modal';
          modal.innerHTML = 
            '<div class="admin-confirm-content">' +
              '<div class="admin-confirm-title">🚫 Ban User</div>' +
              '<div class="admin-confirm-message">Are you sure you want to ban <strong>' + MHV.escapeHtml(targetScreenName) + '</strong>? They will no longer be able to send chat messages.</div>' +
              '<div class="admin-confirm-buttons">' +
                '<button class="admin-confirm-btn admin-confirm-btn-cancel">Cancel</button>' +
                '<button class="admin-confirm-btn admin-confirm-btn-confirm">Ban User</button>' +
              '</div>' +
            '</div>';
          
          document.body.appendChild(modal);
          
          // Cancel button
          modal.querySelector('.admin-confirm-btn-cancel').addEventListener('click', function() {
            modal.remove();
          });
          
          // Confirm button
          modal.querySelector('.admin-confirm-btn-confirm').addEventListener('click', function() {
            socket.emit('admin-ban-user', { 
              customerId: targetCustomerId, 
              screenName: targetScreenName 
            });
            modal.remove();
            addNotification('User ' + MHV.escapeHtml(targetScreenName) + ' has been banned', 'system');
          });
          
          // Click outside to close
          modal.addEventListener('click', function(e) {
            if (e.target === modal) {
              modal.remove();
            }
          });
        }

        // Add system/notification message
        // Set isHtml=true to prevent escaping (for formatted content like join messages)
        // serverId is optional - if provided, use it for delete functionality (server's message ID)
        function addNotification(text, type, isHtml, serverId, extraClass) {
          if (!messagesContainer) return;

          // Check scroll position BEFORE adding content (important for accurate detection)
          var wasNearBottom = isNearBottom();

          var className = 'custom-chat-notification';
          if (type === 'system') {
            className = 'custom-chat-system';
          } else if (type === 'announcement') {
            className = 'custom-chat-announcement';
          }
          if (extraClass) className += ' ' + extraClass;
          
          // Announcements / isHtml carry intentional admin formatting, so they are not
          // fully escaped — but they must NOT be injected raw (a compromised/hijacked
          // admin surface could smuggle <script>/<img onerror>/javascript: links to ALL
          // viewers). Route them through the same DOMParser allow-list sanitizer the lot
          // descriptions use: keeps bold/links/lists, strips scripts + event handlers +
          // dangerous URLs. Plain messages stay text-escaped. (SEC — announcement sink.)
          var content = (type === 'announcement' || isHtml) ? sanitizeLotDescription(text) : MHV.escapeHtml(text);
          
          // Use server ID if provided, otherwise generate a local ID (for local-only messages like welcome)
          var uniqueId = serverId || ('ann-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          
          // Add pin and delete buttons for admins on announcements
          var adminBtnsHtml = '';
          if (isAdmin && type === 'announcement') {
            // Extract plain text for pinning (strip any HTML/emojis prefix)
            var plainText = text.replace(/^📢\s*/, '').trim();
            adminBtnsHtml = '<div class="announcement-admin-btns">' +
              '<button class="announcement-pin-btn" data-announcement-id="' + uniqueId + '" data-announcement-text="' + MHV.escapeAttr(plainText) + '" title="Pin announcement">📌</button>' +
              '<button class="announcement-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>' +
              '</div>';
          }
          
          var html = '<div class="' + className + '" id="' + uniqueId + '" data-message-id="' + uniqueId + '">' + adminBtnsHtml + content + '</div>';
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Attach admin handlers for announcements
          if (isAdmin && type === 'announcement') {
            var el = document.getElementById(uniqueId);
            if (el) {
              // Pin button handler
              var pinBtn = el.querySelector('.announcement-pin-btn');
              if (pinBtn) {
                pinBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  var announcementText = this.dataset.announcementText || '';
                  // Capture original HTML for pinned display (preserves original styling)
                  var clone = el.cloneNode(true);
                  var adminBtns = clone.querySelector('.announcement-admin-btns');
                  if (adminBtns) adminBtns.remove();
                  // Use outerHTML to preserve the element's class and original styling
                  var originalHtml = clone.outerHTML;
                  
                  socket.emit('admin-pin-message', {
                    messageId: uniqueId,
                    type: 'announcement',
                    screenName: 'ANNOUNCEMENT',
                    text: announcementText,
                    originalHtml: originalHtml,
                    timestamp: Date.now()
                  });
                });
              }
              // Delete button handler
              var deleteBtn = el.querySelector('.announcement-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  socket.emit('admin-delete-message', { messageId: uniqueId });
                });
              }
            }
          }
          
          // Use scroll position captured before adding content
          // System messages always scroll, announcements respect user position
          if (type === 'system') {
            scrollToBottom();
          } else if (wasNearBottom) {
            scrollToBottom();
          } else {
            showNewMessagesIndicator();
          }
        }
        
        // Render sold/pass announcement (for history and live)
        function renderSoldPassAnnouncement(data) {
          if (!messagesContainer) return;
          
          var uniqueId = data.id || ('sp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          var isSold = data.type === 'sold';
          
          // Build the announcement HTML
          var html = '<div class="custom-chat-sold-pass-announcement ' + (isSold ? 'sold' : 'pass') + '" id="' + uniqueId + '" data-message-id="' + uniqueId + '">';
          
          // Add delete button for admins
          if (isAdmin) {
            html += '<button class="sold-pass-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>';
          }
          
          // Main text (bold)
          if (isSold) {
            html += '<div class="sold-pass-text">🔨🔨🔨 <strong>SOLD</strong> 🔨🔨🔨</div>';
          } else {
            html += '<div class="sold-pass-text">⏭️⏭️⏭️ <strong>PASSED</strong> ⏭️⏭️⏭️</div>';
          }
          
          // Lot title on new line in white (if available)
          if (data.lotTitle) {
            html += '<div class="sold-pass-lot-title">' + MHV.escapeHtml(data.lotTitle) + '</div>';
          }
          
          html += '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Attach delete handler for admin
          if (isAdmin) {
            var el = document.getElementById(uniqueId);
            if (el) {
              var deleteBtn = el.querySelector('.sold-pass-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  socket.emit('admin-delete-message', { messageId: uniqueId });
                });
              }
            }
          }
        }

        // Render a live-bid card ("SO-AND-SO BID $X") — Phase 2. Modeled on the
        // sold/pass announcement so it reuses the admin delete/retract path
        // (data-message-id + admin-delete-message → admin-message-deleted).
        function renderLiveBidAnnouncement(data) {
          if (!messagesContainer) return;

          var uniqueId = data.id || ('lb-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          var amt = Number(data.bidAmount || 0);
          var outbid = data.isHighBidder === false;
          // Resolve the bidder through the shared resolver → "First L. · screen name · #num".
          // Fall back to the pre-resolved displayName for older/backward-compat cards.
          var hasCanonical = (data.screenName !== undefined || data.firstName !== undefined || data.hideName !== undefined);
          var disp = (hasCanonical && MHV.chatDisplayName)
            ? MHV.chatDisplayName({ screenName: data.screenName, firstName: data.firstName, lastName: data.lastName, bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: isAdmin })
            : { primary: data.displayName || 'Bidder', secondary: '' };
          var name = MHV.escapeHtml(disp.primary || data.displayName || 'Bidder');
          var aliasHtml = disp.secondary ? ' <span class="live-bid-alias">' + MHV.escapeHtml(disp.secondary) + '</span>' : '';
          var numHtml = data.bidderNumber ? ' <span class="live-bid-cardnum">#' + MHV.escapeHtml(String(data.bidderNumber)) + '</span>' : '';
          // Show cents only when non-zero.
          var money = function (n) { n = Number(n || 0); return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); };

          // OUT card (item 7): "🚪 SO-AND-SO is OUT" — a bidder declared out (or an
          // absentee proxy was outbid, auto-posted with the ABSENTEE tag). Reuses the
          // resolved name/alias/number above.
          if (data.isOut) {
            // Same three-box card as a bid — [ bidder ] · [ is / OUT ] · [ On / Lot XXX ] —
            // just red, with a single ❌ as the bidder icon and "OUT" replacing the amount.
            var oAbs = !!data.isAbsentee;
            var oAbsTag = oAbs ? ' <span class="live-bid-abstag">ABSENTEE</span>' : '';
            var oScreen = disp.secondary ? '<span class="live-bid-alias">' + MHV.escapeHtml(disp.secondary) + '</span>' : '';
            var oBadge = data.badge ? '<span class="live-bid-badge">' + MHV.escapeHtml(data.badge) + '</span>' : '';
            var oNum = data.bidderNumber ? '<span class="live-bid-cardnum">#' + MHV.escapeHtml(String(data.bidderNumber)) + '</span>' : '';
            var oIdRow = (oScreen || oBadge || oNum) ? '<div class="live-bid-idrow">' + oScreen + oBadge + oNum + '</div>' : '';
            var oLotBox = '';
            if (data.lotNumber) {
              var oLotDigits = String(data.lotNumber).replace(/[^0-9]/g, '');
              var oLotStr = oLotDigits ? oLotDigits.padStart(3, '0') : String(data.lotNumber);
              oLotBox = '<div class="live-bid-box live-bid-box-lot">' +
                '<span class="live-bid-boxhdr">On</span>' +
                '<span class="live-bid-lotval">Lot ' + MHV.escapeHtml(oLotStr) + '</span>' +
              '</div>';
            }
            var oHtml = '<div class="custom-chat-live-bid-announcement out' + (oAbs ? ' absentee' : '') +
              '" id="' + uniqueId + '" data-message-id="' + MHV.escapeAttr(uniqueId) + '">';
            if (isAdmin) {
              oHtml += '<button class="sold-pass-delete-btn" data-message-id="' + MHV.escapeAttr(uniqueId) + '" title="Delete">✕</button>';
            }
            oHtml += '<div class="live-bid-text live-bid-3box">' +
              '<div class="live-bid-box live-bid-box-who">' +
                '<div class="live-bid-nameline">' +
                  '<span class="live-bid-icon" aria-hidden="true">❌</span>' +
                  '<strong class="live-bid-name">' + name + '</strong>' + oAbsTag +
                '</div>' +
                oIdRow +
              '</div>' +
              '<div class="live-bid-box live-bid-box-amt live-bid-box-out">' +
                '<span class="live-bid-boxhdr">is</span>' +
                '<span class="live-bid-amt live-bid-outval">OUT</span>' +
              '</div>' +
              oLotBox +
            '</div>';
            oHtml += '</div>';
            messagesContainer.insertAdjacentHTML('beforeend', oHtml);
            if (isAdmin) {
              var oEl = document.getElementById(uniqueId);
              var oDel = oEl && oEl.querySelector('.sold-pass-delete-btn');
              if (oDel) oDel.addEventListener('click', function (e) { e.stopPropagation(); socket.emit('admin-delete-message', { messageId: uniqueId }); });
            }
            return;
          }

          var isAbs = !!data.isAbsentee;
          // Three-box card: [ bidder ] · [ BID / $amount ] · [ On / Lot XXX ].
          // Left box mirrors the join — First L. on line 1; screen name (yellow) + badge +
          // bidder number (blue badge) on line 2. Middle = the bid amount under a "BID"
          // header. Right = the lot under an "On" header (omitted when there's no lot).
          var absTag = isAbs ? ' <span class="live-bid-abstag">ABSENTEE</span>' : '';
          var bScreen = disp.secondary ? '<span class="live-bid-alias">' + MHV.escapeHtml(disp.secondary) + '</span>' : '';
          var bBadge = data.badge ? '<span class="live-bid-badge">' + MHV.escapeHtml(data.badge) + '</span>' : '';
          var bNum = data.bidderNumber ? '<span class="live-bid-cardnum">#' + MHV.escapeHtml(String(data.bidderNumber)) + '</span>' : '';
          var idRow = (bScreen || bBadge || bNum) ? '<div class="live-bid-idrow">' + bScreen + bBadge + bNum + '</div>' : '';
          var lotBox = '';
          if (data.lotNumber) {
            var lotDigits = String(data.lotNumber).replace(/[^0-9]/g, '');
            var lotStr = lotDigits ? lotDigits.padStart(3, '0') : String(data.lotNumber);
            lotBox = '<div class="live-bid-box live-bid-box-lot">' +
              '<span class="live-bid-boxhdr">On</span>' +
              '<span class="live-bid-lotval">Lot ' + MHV.escapeHtml(lotStr) + '</span>' +
            '</div>';
          }
          var html = '<div class="custom-chat-live-bid-announcement' + (outbid ? ' outbid' : '') + (isAbs ? ' absentee' : '') +
            '" id="' + uniqueId + '" data-message-id="' + MHV.escapeAttr(uniqueId) + '">';
          if (isAdmin) {
            html += '<button class="sold-pass-delete-btn" data-message-id="' + MHV.escapeAttr(uniqueId) + '" title="Retract">✕</button>';
          }
          html += '<div class="live-bid-text live-bid-3box">' +
            '<div class="live-bid-box live-bid-box-who">' +
              '<div class="live-bid-nameline">' +
                '<span class="live-bid-icon" aria-hidden="true">' + (isAbs ? '🏁' : '💵') + '</span>' +
                '<strong class="live-bid-name">' + name + '</strong>' + absTag +
              '</div>' +
              idRow +
            '</div>' +
            '<div class="live-bid-box live-bid-box-amt">' +
              '<span class="live-bid-boxhdr">BID</span>' +
              '<span class="live-bid-amt">' + money(amt) + '</span>' +
              (data.maxxed ? '<span class="live-bid-maxxed">⭐ MAXXED</span>' : '') +
            '</div>' +
            lotBox +
          '</div>';
          if (!isAbs && outbid && Number(data.currentBid) > 0) {
            html += '<div class="live-bid-sub">Outbid — current bid ' + money(data.currentBid) + '</div>';
          }
          html += '</div>';

          messagesContainer.insertAdjacentHTML('beforeend', html);

          if (isAdmin) {
            var el = document.getElementById(uniqueId);
            if (el) {
              var deleteBtn = el.querySelector('.sold-pass-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  // Full undo: rolls back the bid across the system (engine + bar) AND
                  // removes this card. Falls back to a plain card delete if we lack the
                  // retract targets (older cards).
                  if (data.productId && data.bidderNumber) {
                    socket.emit('undo-live-bid', {
                      messageId: uniqueId,
                      productId: data.productId,
                      bidderNumber: data.bidderNumber,
                      bidAmount: data.bidAmount
                    });
                  } else {
                    socket.emit('admin-delete-message', { messageId: uniqueId });
                  }
                });
              }
            }
          }
        }

        // Render a "🎉 SO-AND-SO won $X" winner card (fires on a committed sale) and
        // trigger the existing fireworks. Modeled on the sold/pass card.
        function renderWinnerAnnouncement(data, celebrate) {
          if (!messagesContainer) return;
          var uniqueId = data.id || ('win-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          // Resolve "First L. · screen name · #number" via the shared resolver (fallback
          // to the pre-resolved displayName for older/backward-compat cards).
          var wHasCanonical = (data.screenName !== undefined || data.firstName !== undefined || data.hideName !== undefined);
          var wDisp = (wHasCanonical && MHV.chatDisplayName)
            ? MHV.chatDisplayName({ screenName: data.screenName, firstName: data.firstName, lastName: data.lastName, bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: isAdmin })
            : { primary: data.displayName || 'Winner', secondary: '' };
          var name = MHV.escapeHtml(wDisp.primary || data.displayName || 'Winner');
          var wAlias = wDisp.secondary ? ' <span class="live-bid-alias">' + MHV.escapeHtml(wDisp.secondary) + '</span>' : '';
          var wNum = data.bidderNumber ? ' <span class="live-bid-cardnum">#' + MHV.escapeHtml(String(data.bidderNumber)) + '</span>' : '';
          var money = function (n) { n = Number(n || 0); return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2)); };

          var html = '<div class="custom-chat-winner-announcement" id="' + uniqueId + '" data-message-id="' + MHV.escapeAttr(uniqueId) + '">';
          if (isAdmin) {
            html += '<button class="sold-pass-delete-btn" data-message-id="' + MHV.escapeAttr(uniqueId) + '" title="Delete">✕</button>';
          }
          html += '<div class="winner-text">🎉 <strong>' + name + '</strong>' + wAlias + wNum + ' won ' + money(data.amount) + '! 🎉</div>';
          if (data.lotTitle) {
            html += '<div class="winner-lot-title">' + MHV.escapeHtml(data.lotTitle) + '</div>';
          }
          html += '</div>';
          messagesContainer.insertAdjacentHTML('beforeend', html);

          if (isAdmin) {
            var wel = document.getElementById(uniqueId);
            var wdel = wel && wel.querySelector('.sold-pass-delete-btn');
            if (wdel) wdel.addEventListener('click', function (e) { e.stopPropagation(); socket.emit('admin-delete-message', { messageId: uniqueId }); });
          }
          if (celebrate && typeof createSoldCelebration === 'function') createSoldCelebration();
        }

        // Add expandable description notification (3 lines, click to expand)
        function addDescriptionNotification(htmlContent, groupId, groupPosition) {
          if (!messagesContainer) return;
          
          // Check scroll position BEFORE adding content
          var wasNearBottom = isNearBottom();
          
          // Strip HTML tags for plain text display, preserving line breaks
          var tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          var plainText = tempDiv.textContent || tempDiv.innerText || '';
          plainText = plainText.trim();
          
          if (!plainText) return;
          
          var uniqueId = 'desc-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
          
          // Build class with group position
          var className = 'custom-chat-announcement custom-chat-description';
          if (groupPosition && groupPosition !== 'only') {
            className += ' announcement-group-' + groupPosition;
          }
          
          var dataAttr = groupId ? ' data-group-id="' + groupId + '"' : '';
          
          // Use wrapper div for collapse state, content and button inline
          var html = '<div class="' + className + '" id="' + uniqueId + '"' + dataAttr + '>' +
            '<div class="description-wrapper collapsed">' +
              '<span class="description-content">' + MHV.escapeHtml(plainText) + '</span>' +
              '<button class="description-expand-btn" data-act="toggle-description" data-p0="' + MHV.escapeAttr(uniqueId) + '">more</button>' +
            '</div>' +
          '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Check if content is short enough to not need expand button
          var descEl = document.getElementById(uniqueId);
          if (descEl) {
            var wrapperEl = descEl.querySelector('.description-wrapper');
            var contentEl = descEl.querySelector('.description-content');
            var btnEl = descEl.querySelector('.description-expand-btn');
            if (wrapperEl && contentEl && btnEl) {
              // Temporarily remove collapsed to measure full height
              wrapperEl.classList.remove('collapsed');
              var fullHeight = contentEl.scrollHeight;
              wrapperEl.classList.add('collapsed');
              var collapsedHeight = contentEl.clientHeight;
              
              // If content fits in 3 lines, hide the button
              if (fullHeight <= collapsedHeight + 5) {
                btnEl.style.display = 'none';
              }
            }
          }
          
          // Auto-scroll if user was near bottom, otherwise show indicator
          if (wasNearBottom) {
            scrollToBottom();
          } else {
            showNewMessagesIndicator();
          }
        }
        
        // Global function to toggle description expand/collapse
        window.toggleDescription = function(id, evt) {
          var sectionEl = document.getElementById(id);
          if (!sectionEl) return;

          var wrapperEl = sectionEl.querySelector('.lot-description-wrapper');
          var btnEl = sectionEl.querySelector('.description-expand-btn');

          if (wrapperEl && btnEl) {
            if (wrapperEl.classList.contains('collapsed')) {
              wrapperEl.classList.remove('collapsed');
              btnEl.textContent = 'less';
            } else {
              wrapperEl.classList.add('collapsed');
              btnEl.textContent = 'more';
            }
            // Prevent event from bubbling
            var e = evt || (typeof event !== 'undefined' ? event : null);
            if (e) e.stopPropagation();
          }
        };

        // Send message
        function sendMessage() {
          if (!inputEl || !isLoggedIn) return;
          
          // Check if chat is closed
          if (chatContainer.classList.contains('chat-closed')) {
            return;
          }
          
          var message = inputEl.value.trim();
          if (!message) return;

          // Check if admin is using "Chat As" feature
          var chatAsSelect = chatContainer.querySelector('#admin-chat-as');
          var chatAsValue = chatAsSelect ? chatAsSelect.value : 'self';
          
          if (chatAsValue === 'admin') {
            // Send as admin announcement (like lot announcement)
            socket.emit('admin-announcement', {
              message: message,
              timestamp: Date.now()
            });
          } else if (chatAsValue === 'absentee') {
            // Chat as absentee bidder
            socket.emit('chat-message', {
              message: message,
              screenName: 'ABSENTEE BID',
              firstName: '',
              badge: '🏁',
              bidderNumber: '',
              profileImage: 'https://cdn.shopify.com/s/files/1/0073/9101/9067/files/absentee-ghost.png?v=1768275196',
              customerId: 'absentee',
              timestamp: Date.now(),
              isAbsentee: true
            });
          } else {
            // Normal message as self.
            var sendNormalChat = function () {
              socket.emit('chat-message', {
                message: message,
                screenName: chatContainer.dataset.customerName || customerName,
                firstName: chatContainer.dataset.customerFirstName || customerFirstName,
                badge: customerBadge,
                bidderNumber: customerBidderNumber,
                profileImage: chatContainer.dataset.customerImage || customerImage,
                customerId: customerId,
                timestamp: Date.now()
              });
            };
            // Smart bid detection (item 4): if this looks like a bid AND bidding is live
            // for me, prompt "Confirm $X bid?" in the bar instead of sending it as chat.
            if (window.MHV_LIVEBAR && typeof window.MHV_LIVEBAR.maybePromptBid === 'function'
                && window.MHV_LIVEBAR.maybePromptBid(message, sendNormalChat)) {
              inputEl.value = '';
              sendBtn.disabled = true;
              return;
            }
            sendNormalChat();
          }

          // Clear input and hide keyboard on mobile
          inputEl.value = '';
          sendBtn.disabled = true;
          
          // On mobile, blur to hide keyboard after sending
          var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          if (isMobile) {
            inputEl.blur();
          } else {
            // On desktop, keep focus for quick follow-up messages
            inputEl.focus();
          }
        }

        // Connection events. NOTE: Socket.IO fires 'connect' on EVERY (re)connection,
        // so this re-joins + re-fetches state after a drop, which is what makes
        // recovery automatic.
        socket.on('connect', function() {
          setChatConnected(true);

          // Clear loading state
          if (loadingEl) {
            loadingEl.style.display = 'none';
          }

          // Add connected notification
          addNotification('Connected to chat', 'system');
          
          // Only emit new-user if this is a standalone socket (not shared with parent page)
          // The parent page (e.g., main-host.liquid) already emits new-user for shared sockets
          if (!isSharedSocket) {
            socket.emit('new-user', {
              isLoggedIn: isLoggedIn,
              customerId: customerId,
              firstName: customerFirstName,
              lastName: customerLastName,
              screenName: customerName,
              bidderNumber: customerBidderNumber,
              badge: customerBadge,
              deviceInfo: deviceInfo
            });
          }

          // Request chat history (always needed)
          socket.emit('request-chat-history');
          
          // Request pinned message (always needed)
          socket.emit('request-pinned-message');
          
          // Request BIN state (for BIN list button and items)
          socket.emit('request-bin-state');
        });

        // If socket is already connected (shared socket), manually trigger initialization
        if (isSharedSocket && socket.connected) {
          console.log('📡 Chat widget: Socket already connected, initializing chat');
          setChatConnected(true);
          if (loadingEl) {
            loadingEl.style.display = 'none';
          }
          addNotification('Connected to chat', 'system');
          socket.emit('request-chat-history');
          socket.emit('request-pinned-message');
          socket.emit('request-bin-state');
        }

        socket.on('connect_error', function(error) {
          console.error('❌ Custom Chat: Connection error:', error);
          if (loadingEl) {
            loadingEl.innerHTML = '<p>Connection error. Retrying...</p>';
            loadingEl.style.display = 'flex';
          }
        });

        socket.on('disconnect', function(reason) {
          setChatConnected(false);
          addNotification('Disconnected from chat', 'system');
        });

        // 'connect' (above) already handles re-join/re-fetch on reconnect; this is
        // just the user-facing confirmation.
        socket.on('reconnect', function() {
          setChatConnected(true);
          addNotification('Reconnected to chat', 'system');
        });

        // Surface ongoing retry attempts in the banner (keeps it honest if the
        // server is taking a while to come back).
        socket.io.on('reconnect_attempt', function(n) {
          if (!chatIsConnected) statusBar.textContent = '⚠️ Reconnecting to chat… (attempt ' + n + ')';
        });

        // Chat message events
        // Own-message styling. Since Q27 reached chat the public payload carries no
        // customer id; it carries a keyed `senderKey` instead, and this socket is told
        // its own key in chat-state. Staff still receive the id, so that comparison is
        // kept as the fallback for them.
        socket.on('chat-message', function(data) {
          var isOwnMessage = (mySenderKey && data.senderKey === mySenderKey) ||
            (!!data.customerId && data.customerId === customerId);
          addMessage(data, isOwnMessage);
        });

        // Lot announcement buffer - collect parts and render as single div
        var lotAnnouncementBuffer = {};
        
        // Lot announcement events (from host loading new items)
        socket.on('lot-announcement', function(data) {
          
          // If no groupId, render immediately as standalone
          if (!data.groupId) {
            if (data.isDescription) {
              addDescriptionNotification(data.message);
            } else {
              addNotification(data.message, 'announcement', false, data.id);
            }
            return;
          }
          
          // Buffer grouped announcements
          if (!lotAnnouncementBuffer[data.groupId]) {
            lotAnnouncementBuffer[data.groupId] = { title: null, dimensions: null, description: null, serverId: null };
          }
          
          var buffer = lotAnnouncementBuffer[data.groupId];
          
          if (data.groupPosition === 'first' || data.groupPosition === 'only') {
            buffer.title = data.message;
            buffer.serverId = data.id; // Store the server's ID for the title message
          } else if (data.isDimensions) {
            buffer.dimensions = data.message;
          } else if (data.isDescription) {
            buffer.description = data.message;
          }
          
          // Render when we receive the last part (or only part)
          if (data.groupPosition === 'last' || data.groupPosition === 'only') {
            renderLotAnnouncement(buffer, data.groupId);
            delete lotAnnouncementBuffer[data.groupId];
          }
        });
        
        // Dimensions-only announcement (from host "Send to Chat" button)
        socket.on('dimensions-announcement', function(data) {
          if (data.dimensions && messagesContainer) {
            // Check scroll position BEFORE adding content
            var wasNearBottom = isNearBottom();
            
            // Use server's ID if available, otherwise generate local ID
            var uniqueId = data.id || ('dims-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
            var html = '<div class="custom-chat-lot-announcement" id="' + uniqueId + '" data-message-id="' + uniqueId + '">';
            
            // Add admin buttons (pin and delete) for admins
            if (isAdmin) {
              html += '<div class="lot-announcement-admin-btns">';
              html += '<button class="lot-announcement-pin-btn" data-lot-id="' + uniqueId + '" data-lot-title="' + MHV.escapeAttr(data.dimensions) + '" title="Pin dimensions">📌</button>';
              html += '<button class="lot-announcement-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>';
              html += '</div>';
            }
            
            html += '<span class="announcement-label">📐 DIMENSIONS</span>';
            html += '<div class="lot-dimensions" style="margin-top: 4px;">' + formatDimensionsHtml(data.dimensions) + '</div>';
            html += '</div>';
            
            var div = document.createElement('div');
            div.innerHTML = html;
            messagesContainer.appendChild(div.firstChild);
            
            // Attach admin handlers for dimensions
            if (isAdmin) {
              var dimsEl = document.getElementById(uniqueId);
              if (dimsEl) {
                // Pin button handler
                var pinBtn = dimsEl.querySelector('.lot-announcement-pin-btn');
                if (pinBtn) {
                  pinBtn.addEventListener('click', function(e) {
                    e.stopPropagation();
                    var dimsText = this.dataset.lotTitle || '';
                    // Capture original HTML for pinned display (preserves original styling)
                    var clone = dimsEl.cloneNode(true);
                    var adminBtns = clone.querySelector('.lot-announcement-admin-btns');
                    if (adminBtns) adminBtns.remove();
                    // Use outerHTML to preserve the element's class and original styling
                    var originalHtml = clone.outerHTML;
                    
                    socket.emit('admin-pin-message', {
                      messageId: uniqueId,
                      type: 'announcement',
                      screenName: 'DIMENSIONS',
                      text: dimsText,
                      originalHtml: originalHtml,
                      timestamp: Date.now()
                    });
                  });
                }
                // Delete button handler
                var deleteBtn = dimsEl.querySelector('.lot-announcement-delete-btn');
                if (deleteBtn) {
                  deleteBtn.addEventListener('click', function(e) {
                    e.stopPropagation();
                    socket.emit('admin-delete-message', { messageId: uniqueId });
                  });
                }
              }
            }
            
            // Auto-scroll if user was near bottom, otherwise show indicator
            if (wasNearBottom) {
              scrollToBottom();
            } else {
              showNewMessagesIndicator();
            }
          }
        });
        
        // Render standalone dimensions announcement (for history replay)
        function renderStandaloneDimensions(data) {
          if (!messagesContainer || !data.dimensions) return;
          
          var uniqueId = data.id || ('dims-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          var html = '<div class="custom-chat-lot-announcement" id="' + uniqueId + '" data-message-id="' + uniqueId + '">';
          
          // Add admin buttons (pin and delete) for admins
          if (isAdmin) {
            html += '<div class="lot-announcement-admin-btns">';
            html += '<button class="lot-announcement-pin-btn" data-lot-id="' + uniqueId + '" data-lot-title="' + MHV.escapeAttr(data.dimensions) + '" title="Pin dimensions">📌</button>';
            html += '<button class="lot-announcement-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>';
            html += '</div>';
          }
          
          html += '<span class="announcement-label">📐 DIMENSIONS</span>';
          html += '<span class="announcement-title">' + MHV.escapeHtml(data.dimensions).replace(/\n/g, '<br>') + '</span>';
          html += '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Attach admin handlers for dimensions
          if (isAdmin) {
            var dimsEl = document.getElementById(uniqueId);
            if (dimsEl) {
              // Pin button handler
              var pinBtn = dimsEl.querySelector('.lot-announcement-pin-btn');
              if (pinBtn) {
                pinBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  var dimsText = this.dataset.lotTitle || '';
                  // Capture original HTML for pinned display (preserves original styling)
                  var clone = dimsEl.cloneNode(true);
                  var adminBtns = clone.querySelector('.lot-announcement-admin-btns');
                  if (adminBtns) adminBtns.remove();
                  // Use outerHTML to preserve the element's class and original styling
                  var originalHtml = clone.outerHTML;
                  
                  socket.emit('admin-pin-message', {
                    messageId: uniqueId,
                    type: 'announcement',
                    screenName: 'DIMENSIONS',
                    text: dimsText,
                    originalHtml: originalHtml,
                    timestamp: Date.now()
                  });
                });
              }
              // Delete button handler
              var deleteBtn = dimsEl.querySelector('.lot-announcement-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  socket.emit('admin-delete-message', { messageId: uniqueId });
                });
              }
            }
          }
        }
        
        // Render combined lot announcement
        function renderLotAnnouncement(buffer, groupId) {
          if (!messagesContainer || !buffer.title) return;
          
          // Check scroll position BEFORE adding content
          var wasNearBottom = isNearBottom();
          
          // Use server's ID if available, otherwise generate local ID
          var uniqueId = buffer.serverId || ('lot-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          
          // Build title section
          var titleText = buffer.title.replace('🎁 NOW SHOWING:', '').trim();
          var html = '<div class="custom-chat-lot-announcement" id="' + uniqueId + '" data-group-id="' + groupId + '" data-message-id="' + uniqueId + '">';
          
          // Add admin buttons (pin and delete) for admins
          if (isAdmin) {
            html += '<div class="lot-announcement-admin-btns">';
            html += '<button class="lot-announcement-pin-btn" data-lot-id="' + uniqueId + '" data-lot-title="' + MHV.escapeAttr(titleText) + '" title="Pin this item">📌</button>';
            html += '<button class="lot-announcement-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>';
            html += '</div>';
          }
          
          html += '<span class="announcement-label">🎁 NOW SHOWING</span>';
          html += '<span class="announcement-title">' + MHV.escapeHtml(titleText) + '</span>';
          
          // Add divider and description if present
          if (buffer.description || buffer.dimensions) {
            html += '<hr class="lot-divider">';
          }
          
          // Add description if present (before dimensions)
          if (buffer.description) {
            // Keep HTML for paragraph formatting, but sanitize to a safe subset
            // (DOM allow-list — see sanitizeLotDescription). SEC-61.
            var safeHtml = sanitizeLotDescription(buffer.description);

            if (safeHtml.trim()) {
              html += '<div class="lot-description-section" id="desc-' + uniqueId + '">';
              html += '<div class="lot-description-wrapper collapsed">';
              html += '<span class="description-content">' + safeHtml + '</span>';
              html += '<button class="description-expand-btn" data-act="toggle-description" data-p0="' + MHV.escapeAttr('desc-' + uniqueId) + '">more</button>';
              html += '</div>';
              html += '</div>';
            }
          }
          
          // Add dimensions if present (after description)
          if (buffer.dimensions) {
            html += '<div class="lot-dimensions">' + formatDimensionsHtml(buffer.dimensions) + '</div>';
          }
          
          html += '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Check if description needs the "more" button (only if content overflows)
          if (buffer.description) {
            var sectionEl = document.getElementById('desc-' + uniqueId);
            if (sectionEl) {
              var wrapperEl = sectionEl.querySelector('.lot-description-wrapper');
              var contentEl = sectionEl.querySelector('.description-content');
              var btnEl = sectionEl.querySelector('.description-expand-btn');
              if (wrapperEl && contentEl && btnEl) {
                // Temporarily expand to measure full height
                wrapperEl.classList.remove('collapsed');
                var fullHeight = contentEl.scrollHeight;
                wrapperEl.classList.add('collapsed');
                var collapsedHeight = wrapperEl.clientHeight;
                
                // Hide button if content fits without truncation
                if (fullHeight <= collapsedHeight + 5) {
                  btnEl.style.display = 'none';
                  wrapperEl.classList.remove('collapsed'); // No need to collapse if it fits
                }
              }
            }
          }
          
          // Attach admin handlers for lot announcements
          if (isAdmin) {
            var lotEl = document.getElementById(uniqueId);
            if (lotEl) {
              // Pin button handler
              var pinBtn = lotEl.querySelector('.lot-announcement-pin-btn');
              if (pinBtn) {
                pinBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  var lotTitle = this.dataset.lotTitle || '';
                  // Capture original HTML for pinned display (preserves original styling)
                  var clone = lotEl.cloneNode(true);
                  // Remove admin buttons
                  var adminBtns = clone.querySelector('.lot-announcement-admin-btns');
                  if (adminBtns) adminBtns.remove();
                  // Ensure description is collapsed (but keep expand button for user interaction)
                  var descWrapper = clone.querySelector('.lot-description-wrapper');
                  if (descWrapper) descWrapper.classList.add('collapsed');
                  // Update description section ID to be unique for pinned area (avoid conflicts)
                  var descSection = clone.querySelector('.lot-description-section');
                  if (descSection) {
                    var pinnedDescId = 'pinned-' + descSection.id;
                    descSection.id = pinnedDescId;
                    // Update the onclick handler to use the new ID
                    var expandBtn = clone.querySelector('.description-expand-btn');
                    if (expandBtn) {
                      expandBtn.setAttribute('data-act', 'toggle-description');
                      expandBtn.setAttribute('data-p0', pinnedDescId);
                    }
                  }
                  // Use outerHTML to preserve the element's class and original styling
                  var originalHtml = clone.outerHTML;
                  
                  socket.emit('admin-pin-message', {
                    messageId: uniqueId,
                    type: 'announcement',
                    screenName: 'NOW SHOWING',
                    text: lotTitle,
                    originalHtml: originalHtml,
                    timestamp: Date.now()
                  });
                });
              }
              // Delete button handler
              var deleteBtn = lotEl.querySelector('.lot-announcement-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  socket.emit('admin-delete-message', { messageId: uniqueId });
                });
              }
            }
          }
          
          // Auto-scroll if user was near bottom, otherwise show indicator
          if (wasNearBottom) {
            scrollToBottom();
          } else {
            showNewMessagesIndicator();
          }
        }

        // Chat history
        socket.on('chat-history', function(history) {
          if (messagesContainer && history && history.length > 0) {
            // Clear any existing messages except loading
            var existingMessages = messagesContainer.querySelectorAll('.custom-chat-message, .custom-chat-notification, .custom-chat-system, .custom-chat-announcement, .custom-chat-lot-announcement, .custom-chat-sold-pass-announcement, .custom-chat-live-bid-announcement, .custom-chat-winner-announcement, .custom-chat-bin-announcement, .custom-chat-bin-sold');
            existingMessages.forEach(function(el) { el.remove(); });
            
            // Buffer for grouping lot announcements in history
            var historyLotBuffer = {};
            
            // Add history messages
            history.forEach(function(msg) {
              // Check if it's a BIN item announcement
              if (msg.isBinItem) {
                renderBinAnnouncement(msg);
              }
              // BIN sold announcements are no longer rendered as separate cards
              // (sold info is shown in the original BIN card)
              else if (msg.isBinSold) {
                // Skip - sold info now shown in original BIN card
              }
              // Check if it's a sold/pass announcement
              else if (msg.isSoldPassAnnouncement) {
                // Render sold/pass announcement from history
                renderSoldPassAnnouncement(msg);
              }
              // Check if it's a live-bid card (Phase 2)
              else if (msg.isLiveBidAnnouncement) {
                renderLiveBidAnnouncement(msg);
              }
              // Winner card from history (no fireworks on replay)
              else if (msg.isWinnerAnnouncement) {
                renderWinnerAnnouncement(msg, false);
              }
              // Join notification from history — render the distinct join chip, NOT a
              // plain chat message (reload bug fix). buildJoinHtml is hoisted below.
              else if (msg.isJoin) {
                addNotification(buildJoinHtml(msg), 'notification', true, msg.id, 'custom-chat-join');
              }
              // Check if it's an announcement or description
              else if (msg.isAnnouncement) {
                // Buffer grouped lot announcements
                if (msg.groupId) {
                  if (!historyLotBuffer[msg.groupId]) {
                    historyLotBuffer[msg.groupId] = { title: null, dimensions: null, description: null, serverId: null };
                  }
                  var buf = historyLotBuffer[msg.groupId];
                  if (msg.groupPosition === 'first' || msg.groupPosition === 'only') {
                    buf.title = msg.message;
                    buf.serverId = msg.id; // Store the server's ID
                  } else if (msg.isDimensions) {
                    buf.dimensions = msg.message;
                  } else if (msg.isDescription) {
                    buf.description = msg.message;
                  }
                  // Render when complete
                  if (msg.groupPosition === 'last' || msg.groupPosition === 'only') {
                    renderLotAnnouncement(buf, msg.groupId);
                    delete historyLotBuffer[msg.groupId];
                  }
                } else if (msg.isStandaloneDimensions) {
                  // Standalone dimensions announcement - render with dimensions styling
                  renderStandaloneDimensions(msg);
                } else {
                  // Standalone announcement - pass server's ID for delete functionality
                  addNotification(msg.message, 'announcement', false, msg.id);
                }
              } else {
                var isOwnMessage = (mySenderKey && msg.senderKey === mySenderKey) ||
                  (!!msg.customerId && msg.customerId === customerId);
                addMessage(msg, isOwnMessage);
              }
            });
          }
          
          // Show welcome announcement after history (appears at bottom)
          // Only show once per 6 hour period
          var welcomeAnnouncement = chatContainer.dataset.welcomeAnnouncement;
          if (welcomeAnnouncement && welcomeAnnouncement.trim()) {
            var now = Date.now();
            var lastShown = localStorage.getItem('chatWelcomeLastShown');
            var sixHours = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

            if (!lastShown || (now - parseInt(lastShown)) > sixHours) {
              addNotification('📢 ' + welcomeAnnouncement, 'announcement');
              localStorage.setItem('chatWelcomeLastShown', now.toString());
            }
          }
          
          // Scroll to bottom only on first history load; preserve position on reconnect
          if (!window._chatHistoryLoaded) {
            window._chatHistoryLoaded = true;
            scrollToBottom();
          }
        });

        // ==========================================================================
        // ADMIN EVENT HANDLERS
        // ==========================================================================
        
        // Handle message deleted by admin
        socket.on('admin-message-deleted', function(data) {
          var messageEl = messagesContainer.querySelector('[data-message-id="' + data.messageId + '"]');
          if (messageEl) {
            messageEl.remove();
          }
        });
        
        // Handle chat cleared by admin
        socket.on('admin-chat-cleared', function(data) {
          // Clear all chat messages
          var allMessages = messagesContainer.querySelectorAll('.custom-chat-message');
          allMessages.forEach(function(el) { el.remove(); });
          
          // Clear all admin announcements (📢 messages)
          var allAnnouncements = messagesContainer.querySelectorAll('.custom-chat-announcement');
          allAnnouncements.forEach(function(el) { el.remove(); });
          
          // Clear all lot announcements (NOW SHOWING messages)
          var allLotAnnouncements = messagesContainer.querySelectorAll('.custom-chat-lot-announcement');
          allLotAnnouncements.forEach(function(el) { el.remove(); });
          
          // Clear all SOLD/PASSED announcements
          var allSoldPassAnnouncements = messagesContainer.querySelectorAll('.custom-chat-sold-pass-announcement');
          allSoldPassAnnouncements.forEach(function(el) { el.remove(); });
          
          // Clear all BIN announcements
          var allBinAnnouncements = messagesContainer.querySelectorAll('.custom-chat-bin-announcement, .custom-chat-bin-sold');
          allBinAnnouncements.forEach(function(el) { el.remove(); });
          
          // Clear all notifications (except the one we're about to add)
          var allNotifications = messagesContainer.querySelectorAll('.custom-chat-notification');
          allNotifications.forEach(function(el) { el.remove(); });
          
          addNotification('Chat has been cleared by an admin', 'system');
        });
        
        // Lock this client down the instant we're banned (kicked out of chat + signal
        // the rest of the page so bidding UI disables too). Idempotent.
        var _selfBanned = false;
        function lockSelfOut() {
          if (_selfBanned) return;
          _selfBanned = true;
          // Replace the chat input with a banned notice (can't chat).
          var inputArea = chatContainer.querySelector('.custom-chat-input-area');
          if (inputArea) {
            inputArea.outerHTML = '<div class="custom-chat-banned-notice">🚫 You have been banned. You can no longer chat or bid.</div>';
          }
          // Belt-and-suspenders: disable any remaining input/send controls.
          var inp = chatContainer.querySelector('.custom-chat-input');
          if (inp) { inp.disabled = true; inp.placeholder = 'You have been banned'; }
          var snd = chatContainer.querySelector('.custom-chat-send-btn, #custom-chat-send');
          if (snd) snd.disabled = true;
          // Page-wide signal so live-page / hub bid widgets lock down immediately.
          try { window.MHV_IS_BANNED = true; } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('mhv:user-banned')); } catch (e) {}
        }
        // Targeted (only the banned user's sockets receive this) — most reliable.
        socket.on('you-are-banned', function() { lockSelfOut(); });
        // Broadcast — react only if it's us.
        // Q27: the room is sent the banned sender's KEY, not their customer id; staff
        // still receive the id. Either matches 'me'.
        socket.on('admin-user-banned', function(data) {
          if (!data) return;
          var mine = (data.senderKey && mySenderKey && data.senderKey === mySenderKey) ||
            (data.customerId && String(data.customerId) === String(customerId));
          if (mine) lockSelfOut();
        });
        
        // ==========================================================================
        // PINNED MESSAGE SYSTEM
        // ==========================================================================
        
        var pinnedArea = chatContainer.querySelector('#custom-chat-pinned-area');
        var pinnedContent = chatContainer.querySelector('#pinned-message-content');
        var pinnedPreview = chatContainer.querySelector('#pinned-preview');
        var unpinBtn = chatContainer.querySelector('#pinned-unpin-btn');
        var currentPinnedMessage = null;
        
        // Display pinned message (preserves original styling)
        function displayPinnedMessage(data) {
          if (!pinnedArea || !pinnedContent) return;
          
          currentPinnedMessage = data;
          
          var html = '';
          var previewText = '';

          // SEC-71: NEVER render client-supplied originalHtml — it round-trips
          // through the server unsanitized and would be stored-XSS on every
          // viewer. Always rebuild the pinned display from the escaped
          // structured fields instead.
          if (data.type === 'announcement') {
            html = '<span class="pinned-announcement">' + MHV.escapeHtml(data.text) + '</span>';
          } else {
            html = '<span class="pinned-author">' + MHV.escapeHtml(data.screenName) + ':</span>' +
                   '<span class="pinned-text">' + MHV.escapeHtml(data.text) + '</span>';
          }
          
          // Generate preview text for minimized view
          if (data.type === 'announcement') {
            previewText = data.text || '';
          } else if (data.screenName && data.text) {
            previewText = data.screenName + ': ' + data.text;
          } else if (data.text) {
            previewText = data.text;
          }
          
          // Truncate preview if too long
          if (previewText.length > 50) {
            previewText = previewText.substring(0, 50) + '...';
          }
          
          pinnedContent.innerHTML = html;
          if (pinnedPreview) {
            pinnedPreview.textContent = previewText;
          }
          pinnedArea.classList.add('visible');
        }
        
        // Hide pinned message
        function hidePinnedMessage() {
          if (!pinnedArea) return;
          currentPinnedMessage = null;
          pinnedArea.classList.remove('visible');
          if (pinnedContent) pinnedContent.innerHTML = '';
          if (pinnedPreview) pinnedPreview.textContent = '';
        }
        
        // Handle pinned message from server (on connect or when someone pins)
        socket.on('pinned-message', function(data) {
          if (data && data.text) {
            displayPinnedMessage(data);
          } else {
            hidePinnedMessage();
          }
        });
        
        // Handle message unpinned
        socket.on('message-unpinned', function() {
          hidePinnedMessage();
          addNotification('Pinned message has been removed', 'system');
        });
        
        // Handle new message pinned
        socket.on('message-pinned', function(data) {
          displayPinnedMessage(data);
          addNotification('A message has been pinned', 'system');
        });
        
        // Unpin button handler (admin only)
        if (unpinBtn && isAdmin) {
          unpinBtn.addEventListener('click', function() {
            socket.emit('admin-unpin-message');
          });
        }

        // User join events (dedup: suppress duplicate joins for the same user within 10s)
        var recentJoinTimes = {};
        // Build the inner HTML for a join chip. Shared by the live `user-joined` event AND
        // history rehydration (isJoin messages) so a reloaded join looks identical to a
        // live one — never falls back to the plain chat-message look (reload bug fix).
        function buildJoinHtml(data) {
          var joinDisp = MHV.chatDisplayName({
            screenName: data.screenName, firstName: data.firstName, lastName: data.lastName,
            bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: isAdmin
          });
          var joinName = joinDisp.bidderFallback ? 'Bidder' : joinDisp.primary;
          var joinBadge = data.badge ? '<span class="chat-join-badge">' + MHV.escapeHtml(data.badge) + '</span>' : '';
          var joinNum = data.bidderNumber ? '<span class="chat-join-num">#' + MHV.escapeHtml(data.bidderNumber) + '</span>' : '';
          var joinAlias = joinDisp.secondary ? '<span class="chat-join-alias">' + MHV.escapeHtml(joinDisp.secondary) + '</span>' : '';
          // Line 1 = "First L. joined the chat"; line 2 = screen name + badge + bidder number.
          var line2Inner = joinAlias + joinBadge + joinNum;
          var line2 = line2Inner ? '<span class="chat-join-line2">' + line2Inner + '</span>' : '';
          return '<span class="chat-join-icon" aria-hidden="true">👋</span>' +
            '<span class="chat-join-body">' +
              '<span class="chat-join-line1">' +
                '<span class="chat-join-name">' + MHV.escapeHtml(joinName) + '</span> ' +
                '<span class="chat-join-verb">joined the chat</span>' +
              '</span>' +
              line2 +
            '</span>';
        }

        socket.on('user-joined', function(data) {
          var joinKey = (data.customerId || '') + '|' + (data.bidderNumber || '') + '|' + (data.screenName || '');
          var now = Date.now();
          if (recentJoinTimes[joinKey] && (now - recentJoinTimes[joinKey]) < 10000) return;
          recentJoinTimes[joinKey] = now;
          // Distinct 'custom-chat-join' styling so a join reads differently from a chat message.
          addNotification(buildJoinHtml(data), 'notification', true, undefined, 'custom-chat-join');
        });

        // Set up input handlers (only for logged-in users who aren't banned)
        if (isLoggedIn && !isBanned && inputEl && sendBtn) {
          // Send button click
          sendBtn.addEventListener('click', sendMessage);

          // Enter key to send
          inputEl.addEventListener('keypress', function(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          });

          // Disable send button when input is empty
          inputEl.addEventListener('input', function() {
            sendBtn.disabled = !inputEl.value.trim();
          });
          sendBtn.disabled = true;

          // Hide floating reactions while input is focused (so they don't block typing/bidding)
          inputEl.addEventListener('focus', function() {
            document.body.classList.add('hide-popping-reactions');
          });
          inputEl.addEventListener('blur', function() {
            document.body.classList.remove('hide-popping-reactions');
          });
          
          // ==========================================================================
          // MOBILE KEYBOARD HANDLING - Keep video in place when keyboard opens
          // ==========================================================================
          
          var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          
          if (isMobile) {
            // Track original viewport height
            var originalViewportHeight = window.innerHeight;
            
            // Use visualViewport API if available (better keyboard detection)
            if (window.visualViewport) {
              window.visualViewport.addEventListener('resize', function() {
                var currentHeight = window.visualViewport.height;
                var heightDiff = originalViewportHeight - currentHeight;
                
                // If viewport shrunk significantly, keyboard is probably open
                if (heightDiff > 150) {
                  document.documentElement.classList.add('keyboard-open');
                  // Scroll chat to bottom when keyboard opens
                  setTimeout(function() {
                    scrollToBottom();
                  }, 100);
                  // Scroll input into view after iOS keyboard animation finishes
                  setTimeout(function() {
                    if (document.activeElement === inputEl && inputEl.scrollIntoView) {
                      inputEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                  }, 350);
                } else {
                  document.documentElement.classList.remove('keyboard-open');
                }
              });
            }
            
            // Fallback: detect focus/blur on input
            inputEl.addEventListener('focus', function() {
              document.documentElement.classList.add('keyboard-open');
              setTimeout(function() {
                scrollToBottom();
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
              }, 300);
              // Scroll input into view after iOS keyboard animation finishes
              setTimeout(function() {
                if (inputEl.scrollIntoView) {
                  inputEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
              }, 500);
            });
            
            inputEl.addEventListener('blur', function() {
              document.documentElement.classList.remove('keyboard-open');
              document.body.style.position = '';
              document.body.style.width = '';
            });
          }
        }
        
        // Set up admin handlers
        if (isAdmin) {
          var clearChatBtn = chatContainer.querySelector('#admin-clear-chat');
          if (clearChatBtn) {
            clearChatBtn.addEventListener('click', function() {
              // Show confirmation modal
              var modal = document.createElement('div');
              modal.className = 'admin-confirm-modal';
              modal.innerHTML = 
                '<div class="admin-confirm-content">' +
                  '<div class="admin-confirm-title">🗑️ Clear Chat</div>' +
                  '<div class="admin-confirm-message">Are you sure you want to clear all chat messages? This action cannot be undone.</div>' +
                  '<div class="admin-confirm-buttons">' +
                    '<button class="admin-confirm-btn admin-confirm-btn-cancel">Cancel</button>' +
                    '<button class="admin-confirm-btn admin-confirm-btn-confirm">Clear Chat</button>' +
                  '</div>' +
                '</div>';
              
              document.body.appendChild(modal);
              
              // Cancel button
              modal.querySelector('.admin-confirm-btn-cancel').addEventListener('click', function() {
                modal.remove();
              });
              
              // Confirm button
              modal.querySelector('.admin-confirm-btn-confirm').addEventListener('click', function() {
                socket.emit('admin-clear-chat');
                modal.remove();
              });
              
              // Click outside to close
              modal.addEventListener('click', function(e) {
                if (e.target === modal) {
                  modal.remove();
                }
              });
            });
          }

          // Toggle chat button handler
          var toggleChatBtn = chatContainer.querySelector('#admin-toggle-chat');
          if (toggleChatBtn) {
            toggleChatBtn.addEventListener('click', function() {
              var isChatOpen = toggleChatBtn.classList.contains('chat-open');
              if (isChatOpen) {
                // Close chat
                socket.emit('admin-close-chat');
              } else {
                // Open chat
                socket.emit('admin-open-chat');
              }
            });
          }

          // Admin feature toggle functions (called from pills in liquid)
          window.adminToggleChat = function(enabled) {
            if (enabled) {
              socket.emit('admin-open-chat');
            } else {
              socket.emit('admin-close-chat');
            }
          };
          window.adminToggleGif = function(enabled) {
            socket.emit('admin-toggle-gif', { enabled: enabled });
          };
          window.adminToggleReactions = function(enabled) {
            socket.emit('admin-toggle-reactions', { enabled: enabled });
          };

          // Sync admin chat toggle pill with existing chat state
          var adminChatToggle = document.getElementById('admin-chat-toggle');
          var origUpdateChatState = updateChatState;
          updateChatState = function(isOpen) {
            origUpdateChatState(isOpen);
            if (adminChatToggle) adminChatToggle.checked = isOpen;
          };

          // ==========================================================================
          // ADMIN SELL/PASS BUTTONS
          // ==========================================================================
          
          var sellBtn = chatContainer.querySelector('#admin-sell-btn');
          var passBtn = chatContainer.querySelector('#admin-pass-btn');
          var reopenBtn = chatContainer.querySelector('#admin-reopen-btn');
          var sellDelaySelect = chatContainer.querySelector('#sell-delay-select');

          // After a SOLD, gray the sell button + reveal the small reopen (⟲) button.
          // Cleared on lot change (below) or when the host reopens bidding.
          function clearSoldDone() {
            if (sellBtn) sellBtn.classList.remove('sold-done');
            if (reopenBtn) reopenBtn.hidden = true;
          }
          var sellPassState = {
            isLoading: false,
            wasInterrupted: false,
            timeout: null,
            type: null // 'sell' or 'pass'
          };
          
          // Get selected delay in milliseconds
          function getSellDelay() {
            return sellDelaySelect ? parseInt(sellDelaySelect.value, 10) : 1500;
          }

          // Track if a message arrives during loading
          var messageArrivedDuringLoading = false;

          // Listen for incoming messages to interrupt SELL/PASS
          socket.on('chat-message', function(data) {
            // This listener runs alongside the existing one
            if (sellPassState.isLoading) {
              // A new message arrived during loading - interrupt!
              messageArrivedDuringLoading = true;
              interruptSellPass();
            }
          });

          // Live-bid tracking for the SELL flow (items 5 + 6). Latest DB engine state
          // for the lot on the block, and blocking SOLD when a bid lands.
          var liveBid = { productId: null, currentBid: 0, highBidderNum: '', highBidderSn: '', numberOfBids: 0 };
          // Authoritative snapshot the SELL capture reads — updated ONLY from a real
          // (non-provisional) live-bid-state. During absentee-reclaim pacing the server
          // emits a provisional state showing the losing live bidder high; if the host
          // SELLs during that window we must NOT capture the provisional (bug #10).
          var liveBidAuth = { productId: null, currentBid: 0, highBidderNum: '', highBidderSn: '', numberOfBids: 0 };
          var sellBidBaseline = 0;   // numberOfBids captured when SELL was clicked
          // Suppress the (future/un-stuck) host-page.js continuous selling-widget
          // auto-fill — we now capture ONLY on a successful SOLD (item 5).
          window.__mhvSellOnlyCapture = true;
          socket.on('live-bid-state', function (d) {
            if (!d || !d.productId) return;
            var count = Number(d.numberOfBids) || 0;
            // Lot changed → drop any post-sold gray/reopen state from the previous lot,
            // and reset the authoritative snapshot so a stale prior-lot winner can't flow.
            if (liveBid.productId && String(d.productId) !== liveBid.productId) {
              clearSoldDone();
              liveBidAuth = { productId: null, currentBid: 0, highBidderNum: '', highBidderSn: '', numberOfBids: 0 };
            }
            // A NEW bid during a SELL countdown BLOCKS the sold; the bid comes through (item 6).
            if (sellPassState.isLoading && sellPassState.type === 'sell' && count > sellBidBaseline) {
              messageArrivedDuringLoading = true;   // reuse the existing block flag
              interruptSellPass();
            }
            liveBid = {
              productId: String(d.productId),
              currentBid: Number(d.currentBid || 0),
              highBidderNum: d.highBidderBidderNumber || '',
              highBidderSn: d.highBidderSn || '',
              numberOfBids: count
            };
            // Only real states feed the capture snapshot. Retain the last non-empty bidder
            // for the same lot so a state that arrives with a blank high_bidder doesn't
            // wipe the bidder while keeping the price (bug #10, empty-bidder case).
            if (!d.provisional) {
              var sameLot = liveBidAuth.productId === String(d.productId);
              var num = d.highBidderBidderNumber || (sameLot ? liveBidAuth.highBidderNum : '') || '';
              var sn = d.highBidderSn || (sameLot ? liveBidAuth.highBidderSn : '') || '';
              liveBidAuth = {
                productId: String(d.productId),
                currentBid: Number(d.currentBid || 0),
                highBidderNum: num,
                highBidderSn: sn,
                numberOfBids: count
              };
            }
          });

          // Capture the winning bid + bidder into the selling widget — ONLY here, on a
          // successful SOLD send (item 5). Reads the authoritative (non-provisional)
          // snapshot; dispatches 'input' so updateCustomLotSubmitButton enables Submit
          // (the bidder name resolves on the host's Submit-click lookup).
          function captureSoldToSellingWidget() {
            try {
              var priceEl = document.getElementById('custom-lot-price');
              var bidderEl = document.getElementById('custom-lot-bidder');
              if (priceEl && liveBidAuth.currentBid > 0) {
                priceEl.value = Number(liveBidAuth.currentBid).toFixed(2);
                priceEl.dispatchEvent(new Event('input', { bubbles: true }));
              }
              if (bidderEl && liveBidAuth.highBidderNum) {
                bidderEl.value = liveBidAuth.highBidderNum;
                bidderEl.dispatchEvent(new Event('input', { bubbles: true }));
                bidderEl.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } catch (e) { /* non-critical */ }
          }

          // Get current lot title (from catalog or custom)
          function getCurrentLotTitle() {
            // First check for custom lot title
            if (typeof window.confirmedCustomLotTitle !== 'undefined' && window.confirmedCustomLotTitle) {
              return window.confirmedCustomLotTitle;
            }
            // Then check for catalog lot from showing widget
            if (typeof window.currentShowingProduct !== 'undefined' && window.currentShowingProduct) {
              return window.currentShowingProduct.title || '';
            }
            // Try to get from DOM
            var showingTitle = document.getElementById('showing-lot-title');
            if (showingTitle && showingTitle.textContent !== 'No Product Selected') {
              return showingTitle.textContent;
            }
            return '';
          }

          // Cancel SELL/PASS action (user clicked again during loading)
          function cancelSellPass() {
            if (sellPassState.timeout) {
              clearTimeout(sellPassState.timeout);
              sellPassState.timeout = null;
            }
            
            var targetBtn = sellPassState.type === 'sell' ? sellBtn : passBtn;
            targetBtn.classList.remove('loading');
            
            // Reset loading bar on the target button
            var loadingBar = targetBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
            }
            
            sellPassState.isLoading = false;
            sellPassState.type = null;
            console.log('🚫 SELL/PASS cancelled by user');
          }

          // Start SELL action
          function startSellAction() {
            // If already loading SELL, cancel it
            if (sellPassState.isLoading && sellPassState.type === 'sell') {
              cancelSellPass();
              return;
            }
            // If loading PASS, ignore (don't allow switching)
            if (sellPassState.isLoading) return;

            // The countdown runs for exactly the configured delay (the #sell-delay-select
            // setting). Bid-protection is still active DURING that window: any live bid
            // that lands before the timer fires blocks the sale and lets the bid through
            // (see the live-bid-state guard). No artificial floor — respect the setting.
            var delay = getSellDelay();
            sellBidBaseline = liveBid.numberOfBids;

            sellPassState.isLoading = true;
            sellPassState.type = 'sell';
            messageArrivedDuringLoading = false;
            
            sellBtn.classList.add('loading');
            sellBtn.classList.remove('interrupted');
            
            // Reset loading bar and trigger animation
            var loadingBar = sellBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
              // Force reflow
              loadingBar.offsetHeight;
              loadingBar.style.transition = 'width ' + (delay / 1000) + 's linear';
              loadingBar.style.width = '100%';
            }
            
            // Wait for selected delay then send if not interrupted
            sellPassState.timeout = setTimeout(function() {
              if (!messageArrivedDuringLoading) {
                completeSellAction();
              }
            }, delay);
          }

          // Start PASS action
          function startPassAction() {
            // If already loading PASS, cancel it
            if (sellPassState.isLoading && sellPassState.type === 'pass') {
              cancelSellPass();
              return;
            }
            // If loading SELL, ignore (don't allow switching)
            if (sellPassState.isLoading) return;
            
            var delay = getSellDelay();
            
            sellPassState.isLoading = true;
            sellPassState.type = 'pass';
            messageArrivedDuringLoading = false;
            
            passBtn.classList.add('loading');
            passBtn.classList.remove('interrupted');
            
            // Reset loading bar and trigger animation
            var loadingBar = passBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
              // Force reflow
              loadingBar.offsetHeight;
              loadingBar.style.transition = 'width ' + (delay / 1000) + 's linear';
              loadingBar.style.width = '100%';
            }
            
            // Wait for selected delay then send if not interrupted
            sellPassState.timeout = setTimeout(function() {
              if (!messageArrivedDuringLoading) {
                completePassAction();
              }
            }, delay);
          }

          // Interrupt SELL/PASS due to incoming message
          function interruptSellPass() {
            if (sellPassState.timeout) {
              clearTimeout(sellPassState.timeout);
              sellPassState.timeout = null;
            }
            
            var targetBtn = sellPassState.type === 'sell' ? sellBtn : passBtn;
            targetBtn.classList.remove('loading');
            targetBtn.classList.add('interrupted');
            
            // Reset loading bar on the target button
            var loadingBar = targetBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
            }
            
            // Remove interrupted class after animation completes (6 flashes * 0.15s * 2 = 1.8s)
            setTimeout(function() {
              targetBtn.classList.remove('interrupted');
              sellPassState.isLoading = false;
              sellPassState.type = null;
            }, 900);
          }

          // Complete SELL action - send message
          function completeSellAction() {
            sellBtn.classList.remove('loading');
            sellPassState.isLoading = false;
            sellPassState.type = null;
            
            // Reset loading bar
            var loadingBar = sellBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
            }
            
            var lotTitle = getCurrentLotTitle();
            
            // Send as sold-pass announcement (bold text, white lot title, deletable)
            socket.emit('sold-pass-announcement', {
              type: 'sold',
              lotTitle: lotTitle,
              timestamp: Date.now()
            });
            
            // Emit celebration animation event (broadcast to all viewers)
            socket.emit('sold-celebration', {
              timestamp: Date.now()
            });

            // Capture the winning bid + bidder into the selling widget (item 5).
            captureSoldToSellingWidget();

            // Post-sold: gray the sell button + reveal the small reopen (⟲) button so
            // the host can re-open bidding on this lot if the sale was premature.
            if (sellBtn) sellBtn.classList.add('sold-done');
            if (reopenBtn) reopenBtn.hidden = false;
          }

          // Complete PASS action - send message
          function completePassAction() {
            passBtn.classList.remove('loading');
            sellPassState.isLoading = false;
            sellPassState.type = null;
            
            // Reset loading bar
            var loadingBar = passBtn.querySelector('.loading-bar');
            if (loadingBar) {
              loadingBar.style.transition = 'none';
              loadingBar.style.width = '0%';
            }
            
            var lotTitle = getCurrentLotTitle();
            
            // Send as sold-pass announcement (bold text, white lot title, deletable, no celebration)
            socket.emit('sold-pass-announcement', {
              type: 'pass',
              lotTitle: lotTitle,
              timestamp: Date.now()
            });
          }

          // Set up button click handlers
          if (sellBtn) {
            sellBtn.addEventListener('click', startSellAction);
          }
          if (passBtn) {
            passBtn.addEventListener('click', startPassAction);
          }
          // Reopen bidding on the current lot (undo a premature SOLD).
          if (reopenBtn) {
            reopenBtn.addEventListener('click', function () {
              clearSoldDone();
              socket.emit('open-live-bidding', {});
            });
          }

        }

        // ==========================================================================
        // USER SETTINGS & CONTROLS (available to all users)
        // ==========================================================================
        
        // Text size dropdown handler (shared function for both admin and user selectors)
        function initTextSizeSelector(selector) {
          var textSizeSelect = chatContainer.querySelector(selector);
          if (textSizeSelect) {
            // Load saved text size preference (default to medium)
            var savedTextSize = localStorage.getItem('chatTextSize') || 'medium';
            textSizeSelect.value = savedTextSize;
            chatContainer.classList.add('text-size-' + savedTextSize);
            
            textSizeSelect.addEventListener('change', function() {
              var newSize = this.value;
              // Remove all text size classes
              chatContainer.classList.remove('text-size-teeny', 'text-size-small', 'text-size-medium', 'text-size-large', 'text-size-giant');
              // Add the new size class
              chatContainer.classList.add('text-size-' + newSize);
              // Save preference
              localStorage.setItem('chatTextSize', newSize);
              // Sync both selectors if they exist
              var adminSelect = chatContainer.querySelector('#admin-text-size');
              var userSelect = chatContainer.querySelector('#user-text-size');
              if (adminSelect && adminSelect !== this) adminSelect.value = newSize;
              if (userSelect && userSelect !== this) userSelect.value = newSize;
            });
          }
        }
        
        // Initialize both text size selectors
        initTextSizeSelector('#admin-text-size');
        initTextSizeSelector('#user-text-size');
        
        // Pinned mode handler (Show/Minimize/Hide)
        var pinnedArea = chatContainer.querySelector('#custom-chat-pinned-area');
        var pinnedToggleBtn = chatContainer.querySelector('#pinned-toggle-btn');
        var pinnedHideBtn = chatContainer.querySelector('#pinned-hide-btn');
        
        function applyPinnedMode(mode, saveToStorage) {
          if (!pinnedArea) return;
          // Remove all pinned mode classes
          pinnedArea.classList.remove('pinned-minimize', 'pinned-hide');
          // Apply the new mode class
          if (mode === 'minimize') {
            pinnedArea.classList.add('pinned-minimize');
          } else if (mode === 'hide') {
            pinnedArea.classList.add('pinned-hide');
          }
          // mode === 'show' needs no class (default visible)
          
          // Save to localStorage if requested
          if (saveToStorage !== false) {
            localStorage.setItem('chatPinnedMode', mode);
          }
          
          // Sync settings dropdown
          var pinnedSelect = chatContainer.querySelector('#user-pinned-mode');
          if (pinnedSelect && pinnedSelect.value !== mode) {
            pinnedSelect.value = mode;
          }
        }
        
        function getCurrentPinnedMode() {
          if (pinnedArea) {
            if (pinnedArea.classList.contains('pinned-hide')) return 'hide';
            if (pinnedArea.classList.contains('pinned-minimize')) return 'minimize';
          }
          return 'show';
        }
        
        function initPinnedModeSelector() {
          var pinnedSelect = chatContainer.querySelector('#user-pinned-mode');
          if (pinnedSelect) {
            // Load saved preference (default to show)
            var savedMode = localStorage.getItem('chatPinnedMode') || 'show';
            pinnedSelect.value = savedMode;
            applyPinnedMode(savedMode, false);
            
            pinnedSelect.addEventListener('change', function() {
              var newMode = this.value;
              applyPinnedMode(newMode, true);
            });
          }
        }
        
        // Toggle button (minimize/expand) handler
        if (pinnedToggleBtn) {
          pinnedToggleBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            var currentMode = getCurrentPinnedMode();
            // Toggle between show and minimize
            var newMode = (currentMode === 'minimize') ? 'show' : 'minimize';
            applyPinnedMode(newMode, true);
          });
        }
        
        // Hide button handler
        if (pinnedHideBtn) {
          pinnedHideBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            applyPinnedMode('hide', true);
          });
        }
        
        initPinnedModeSelector();
        
        // Initialize BIN purchase mode selector
        function initBinPurchaseModeSelector() {
          var binSelect = chatContainer.querySelector('#user-bin-purchase-mode');
          if (binSelect) {
            // Load saved preference (default to confirm)
            var savedMode = getBinPurchaseSetting();
            binSelect.value = savedMode;
            
            binSelect.addEventListener('change', function() {
              var newMode = this.value;
              setBinPurchaseSetting(newMode);
              
              // If turning on immediate, show notices on existing BIN items
              if (newMode === 'immediate') {
                document.querySelectorAll('.custom-chat-bin-announcement:not(.sold)').forEach(function(binEl) {
                  if (!binEl.querySelector('.bin-instant-notice')) {
                    var binId = binEl.dataset.binId;
                    var noticeHtml = '<div class="bin-instant-notice">';
                    noticeHtml += '<span>Purchase Immediately On</span>';
                    noticeHtml += '<button class="bin-turn-off-btn" data-bin-id="' + binId + '">Turn Off</button>';
                    noticeHtml += '</div>';
                    binEl.insertAdjacentHTML('beforeend', noticeHtml);
                    
                    // Attach turn off handler
                    var turnOffBtn = binEl.querySelector('.bin-turn-off-btn');
                    if (turnOffBtn) {
                      turnOffBtn.addEventListener('click', function(e) {
                        e.stopPropagation();
                        setBinPurchaseSetting('confirm');
                        document.querySelectorAll('.bin-instant-notice').forEach(function(el) { el.remove(); });
                      });
                    }
                  }
                });
              } else {
                // Remove all instant notices
                document.querySelectorAll('.bin-instant-notice').forEach(function(el) { el.remove(); });
              }
            });
          }
        }
        
        initBinPurchaseModeSelector();
        
        // Default settings button handler
        function initDefaultSettingsButton() {
          var defaultBtn = chatContainer.querySelector('#user-settings-default');
          if (defaultBtn) {
            defaultBtn.addEventListener('click', function(e) {
              e.stopPropagation();
              
              // Reset text size to medium
              localStorage.setItem('chatTextSize', 'medium');
              chatContainer.classList.remove('text-size-teeny', 'text-size-small', 'text-size-medium', 'text-size-large', 'text-size-giant');
              chatContainer.classList.add('text-size-medium');
              var adminSelect = chatContainer.querySelector('#admin-text-size');
              var userSelect = chatContainer.querySelector('#user-text-size');
              if (adminSelect) adminSelect.value = 'medium';
              if (userSelect) userSelect.value = 'medium';
              
              // Reset reactions to enabled (clear snooze)
              localStorage.removeItem('mhv_reactions_snoozed_until');
              localStorage.removeItem('mhv_reactions_disabled');
              if (typeof updateSnoozeState === 'function') {
                updateSnoozeState();
              }
              
              // Reset pinned mode to show
              applyPinnedMode('show', true);
              
              // Reset BIN purchase mode to confirm
              setBinPurchaseSetting('confirm');
              var binSelect = chatContainer.querySelector('#user-bin-purchase-mode');
              if (binSelect) binSelect.value = 'confirm';
              document.querySelectorAll('.bin-instant-notice').forEach(function(el) { el.remove(); });
            });
          }
        }
        
        initDefaultSettingsButton();
        
        // User settings popup handler
        var settingsBtn = chatContainer.querySelector('#user-settings-btn');
        var settingsPopup = chatContainer.querySelector('#user-settings-popup');
        if (settingsBtn && settingsPopup) {
          settingsBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            settingsPopup.classList.toggle('open');
            settingsBtn.classList.toggle('active');
            // When closing, blur the button so mobile browsers drop :hover/:focus
            if (!settingsPopup.classList.contains('open')) {
              settingsBtn.blur();
            }
          });
          
          // Close popup when clicking outside
          document.addEventListener('click', function(e) {
            if (!settingsPopup.contains(e.target) && !settingsBtn.contains(e.target)) {
              settingsPopup.classList.remove('open');
              settingsBtn.classList.remove('active');
            }
          });
          
          // Prevent popup from closing when clicking inside it
          settingsPopup.addEventListener('click', function(e) {
            e.stopPropagation();
          });
        }

        // ==========================================================================
        // SOLD/PASS ANNOUNCEMENTS & CELEBRATION (visible to all users)
        // ==========================================================================
        
        // Listen for sold/pass announcements
        socket.on('sold-pass-announcement', function(data) {
          if (!messagesContainer) return;

          // Check scroll position BEFORE adding content
          var wasNearBottom = isNearBottom();

          // Render the announcement
          renderSoldPassAnnouncement(data);

          // Auto-scroll if user was near bottom
          if (wasNearBottom) {
            scrollToBottom();
          }
        });

        // Live-bid card (Phase 2): "SO-AND-SO BID $X"
        socket.on('live-bid-announcement', function(data) {
          if (!messagesContainer) return;
          var wasNearBottom = isNearBottom();
          renderLiveBidAnnouncement(data);
          if (wasNearBottom) scrollToBottom();
        });

        // Winner card + fireworks on a committed sale (item 6).
        socket.on('winner-announcement', function(data) {
          if (!messagesContainer) return;
          var wasNearBottom = isNearBottom();
          renderWinnerAnnouncement(data, true);
          if (wasNearBottom) scrollToBottom();
        });

        // ==========================================================================
        // LIVE BID BAR (Phase 2) — quick-bid / custom-bid controller above the input.
        // Bound to the lot on the block via the server's live-bid-state broadcast;
        // bids run through the DB engine (place-live-bid) and sync back to every bar.
        // ==========================================================================
        (function initLiveBidBar() {
          var bar = document.getElementById('live-bid-bar');
          if (!bar) return; // absent on the host page / guest view

          var elLot = document.getElementById('lbb-lot');
          var elCurrent = document.getElementById('lbb-current-amt');
          var elLeader = document.getElementById('lbb-leader');
          var elClosed = document.getElementById('lbb-closed');
          var elNoBidder = document.getElementById('lbb-nobidder');
          var elActions = document.getElementById('lbb-actions');
          var elQuickLow = document.getElementById('lbb-quick-low');
          var elQuickHigh = document.getElementById('lbb-quick-high');
          var elCustomToggle = document.getElementById('lbb-custom-toggle');
          var elCustom = document.getElementById('lbb-custom');
          var elCustomInput = document.getElementById('lbb-custom-input');
          var elCustomConfirm = document.getElementById('lbb-custom-confirm');
          var elCustomCancel = document.getElementById('lbb-custom-cancel');
          var elConfirm = document.getElementById('lbb-confirm');
          var elConfirmYes = document.getElementById('lbb-confirm-yes');
          var elConfirmAmt = document.getElementById('lbb-confirm-amt');
          var elConfirmCancel = document.getElementById('lbb-confirm-cancel');
          var elMsg = document.getElementById('lbb-msg');
          var elOverlay = document.getElementById('lbb-highbidder-overlay');
          var elCurrentWrap = document.getElementById('lbb-current-wrap');
          var elChatbid = document.getElementById('lbb-chatbid');
          var elChatbidAmt = document.getElementById('lbb-chatbid-amt');
          var elChatbidYes = document.getElementById('lbb-chatbid-yes');
          var elChatbidNo = document.getElementById('lbb-chatbid-no');
          var elOutbid = document.getElementById('lbb-outbid-overlay');
          var elWon = document.getElementById('lbb-won-overlay');
          var elOut = document.getElementById('lbb-out');
          var elOutConfirm = document.getElementById('lbb-out-confirm');
          var elOutYes = document.getElementById('lbb-out-yes');
          var elOutNo = document.getElementById('lbb-out-no');
          var elJumpback = document.getElementById('lbb-jumpback');
          var elJumpbackBtn = document.getElementById('lbb-jumpback-btn');
          var elJumpAmt = document.getElementById('lbb-jumpback-amt');
          var elReggate = document.getElementById('lbb-reggate');
          var elReggateBtn = document.getElementById('lbb-reggate-btn');
          var elRegistered = document.getElementById('lbb-registered');

          // Per-auction registration gate state. When the auction enforces
          // registration and this viewer isn't registered, a "Register to Bid"
          // panel covers the bidding area — even with no lot loaded — and blocks
          // bids until they register in-page (real-time unlock). Admins (host) are
          // never gated. auctionCode comes from the authoritative locked-session
          // socket event (falls back to the live-page config).
          var reg = {
            enforced: false, registered: false, checked: false,
            auctionCode: (window.MHV_LIVE_CONFIG && window.MHV_LIVE_CONFIG.auctionDateCode) || ''
          };
          function regGateActive() { return reg.enforced && !reg.registered && !isAdmin; }
          // A registered viewer (when enforcement is on) gets a "✓ Registered" badge in
          // place of the register bar, shown until bidding opens on a lot.
          function regBadgeEligible() { return reg.enforced && reg.registered && !isAdmin; }
          function showRegisteredBadge() {
            bar.hidden = false;
            hide(elActions); hide(elClosed); hide(elNoBidder); hide(elConfirm); hide(elCustom);
            hide(elChatbid); hide(elOverlay); hide(elOutbid); hide(elWon); hide(elJumpback); hide(elOutConfirm); hide(elReggate);
            if (elLot) elLot.textContent = st.lotNumber ? ('LOT #' + st.lotNumber) : '';
            if (elCurrentWrap) elCurrentWrap.hidden = true;
            if (elLeader) elLeader.hidden = true;
            show(elRegistered);
          }
          function refreshRegStatus() {
            var code = reg.auctionCode, key = window.MHV_CUSTOMER_KEY;
            if (!code || !key || isAdmin) return;
            fetch(MHV.SERVER_URL + '/auction-register/status?auction=' + encodeURIComponent(code), { headers: { 'X-Customer-Key': key } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (d) {
                if (!d) return;
                reg.enforced = !!d.enforced; reg.registered = !!d.registered; reg.checked = true;
                render();
              })
              .catch(function () {});
          }
          function openRegModal() {
            if (!window.MHVAuctionRegister || !window.MHVAuctionRegister.open) { flash('Registration is unavailable — please refresh.', true); return; }
            window.MHVAuctionRegister.open({ auctionCode: reg.auctionCode, onRegistered: function () {
              reg.registered = true;
              try { if (socket) socket.emit('registration-self-updated', { auctionCode: reg.auctionCode }); } catch (e) {}
              render();
            } });
          }

          var hasBidder = bar.dataset.hasBidder === 'true';
          var myBidder = String(bar.dataset.bidderNumber || '');
          var st = { productId: null, currentBid: 0, minimumBid: 0, ask: 0, isOpen: false, leaderSn: '', leaderNum: '', lotNumber: '', pending: null };
          var submitting = false;
          var submitTimer = null; // failsafe: clears `submitting` if the server never acks
          var chatbidPending = null, chatbidDecline = null;
          var outbidActive = false, outbidTimer = null; // brief red OUTBID! flash (item 3)
          var soldWon = false, soldWonAt = 0, soldWonText = ''; // post-sold "won for" overlay (item 4)
          var lotPassed = false, lotPassedAt = 0; // post-pass "lot passed" overlay (item 11)
          var iHaveBidThisLot = false; // I've placed a bid on the lot on the block (gates OUT, item 7)
          var iAmOut = false;          // I declared OUT on this lot → show "Jump back in" (item 7)
          var outConfirming = false;   // OUT confirm prompt is showing (item 7)
          var jumpBackAmt = 0;         // the amount the "Jump back in" quick-bid re-enters at
          var barEnabled = true; // "Bidding: old/new" toggle — false = OLD (bar hidden, bid in chat)

          // Unified LIVE increment ladder — mirrors liveBidIncrementFor/liveBidIncrementHighFor
          // in the backend engine + the showing widget so the quick bids and the ask never
          // diverge (tweak 1). LOW = first quick bid; HIGH = the bigger second one.
          function liveInc(a) { a = parseFloat(a) || 0; if (a < 10) return 1; if (a < 100) return 5; if (a < 250) return 10; if (a < 500) return 25; if (a < 1000) return 50; return 100; }
          function liveIncHigh(a) { a = parseFloat(a) || 0; if (a < 10) return 5; if (a < 100) return 10; if (a < 250) return 25; if (a < 500) return 50; if (a < 1000) return 100; return 250; }
          // Next ON-GRID bid above `cur` (tweak 2): 22→25, 47→50, 101→110.
          function nextAsk(c) { c = parseFloat(c) || 0; if (c <= 0) return 0; var i = liveInc(c); return Math.floor(c / i) * i + i; }
          function nextAskHigh(c) { c = parseFloat(c) || 0; if (c <= 0) return 0; var i = liveIncHigh(c); return Math.floor(c / i) * i + i; }
          // Minimum advance for a CUSTOM bid — the increment one tier below the current
          // bracket ($5 tier → $1, $10 → $5, $25 → $10, $50 → $25, $100 → $50). Mirrors
          // minLiveAdvanceFor() in the backend engine.
          function minLiveAdvance(c) { c = parseFloat(c) || 0; if (c < 100) return 1; if (c < 250) return 5; if (c < 500) return 10; if (c < 1000) return 25; return 50; }
          // Jump-back-in increment (item 7): HALF the current (low) increment, to entice a
          // smaller re-entry step from a bidder who dropped out ($100 tier → +$50, $5 tier →
          // +$2.50, $1 tier → +$1). Never below $1. This is why .50 bids are allowed — see
          // the half-dollar floor in validBidOrNull + the server.
          function jumpBackInc(c) { var h = liveInc(c) / 2; return h < 1 ? 1 : h; }
          function jumpBackAmount() { var cur = st.currentBid; return cur > 0 ? cur + jumpBackInc(cur) : quickAmounts()[0]; }
          // The two quick-bid amounts [low, high] — both snapped onto the increment grid.
          function quickAmounts() {
            var cur = st.currentBid;
            if (cur > 0) {
              var low = nextAsk(cur);
              var high = nextAskHigh(cur);
              // The server ask is the same nextAsk(current); honor it if it's ever higher
              // (defensive — keeps the low button in sync with the showing widget).
              if (st.ask > low) low = Math.ceil(st.ask);
              if (high <= low) high = low + (liveIncHigh(cur) - liveInc(cur));
              return [low, high];
            }
            // Opening (no bids): the opening bid is the host's ask / lot minimum (else $5);
            // the bigger button is the next grid step above it.
            var base = Math.ceil(st.ask > 0 ? st.ask : (st.minimumBid > 0 ? st.minimumBid : 5));
            return [base, nextAsk(base)];
          }
          // Show cents only when there are any (whole dollars → "$50", else "$2.50").
          function fmt(n) {
            n = Number(n || 0);
            return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
          }
          function show(el) { if (el) el.hidden = false; }
          function hide(el) { if (el) el.hidden = true; }
          function isHighBidder() { return hasBidder && myBidder && String(st.leaderNum) === myBidder; }

          function flash(msg, isError) {
            if (!elMsg) return;
            elMsg.textContent = msg;
            elMsg.classList.toggle('error', !!isError);
            elMsg.hidden = false;
            clearTimeout(elMsg._t);
            elMsg._t = setTimeout(function () { elMsg.hidden = true; }, 3500);
          }

          function resetToQuick() {
            hide(elConfirm); hide(elCustom);
            st.pending = null;
            submitting = false;
            if (submitTimer) { clearTimeout(submitTimer); submitTimer = null; }
          }

          // Item 3: when I go from high bidder → outbid on an open lot, flash a red
          // OUTBID! badge for ~750ms in place of the high-bidder badge, then re-render
          // (which reveals the bid buttons so I can bid again).
          function triggerOutbidFlash() {
            outbidActive = true;
            if (outbidTimer) clearTimeout(outbidTimer);
            outbidTimer = setTimeout(function () { outbidActive = false; outbidTimer = null; render(); }, 750);
            render();
          }

          function render() {
            // Re-anchor the "new messages" arrow after this render settles the bar's
            // height/visibility (item 1 — dynamic offset, never a fixed guess).
            if (window.MHV_repositionChatIndicator) requestAnimationFrame(window.MHV_repositionChatIndicator);
            // OLD bidding mode: hide the live bid bar entirely — customers bid in chat.
            if (!barEnabled) { bar.hidden = true; return; }

            // Registration gate — covers the bidding area whenever the auction
            // enforces registration and this viewer isn't registered. Shown even
            // with NO lot loaded (waiting for the auctioneer), so an unregistered
            // viewer is prompted to register the moment they arrive.
            if (regGateActive()) {
              bar.hidden = false;
              hide(elActions); hide(elClosed); hide(elNoBidder); hide(elConfirm); hide(elCustom);
              hide(elChatbid); hide(elOverlay); hide(elOutbid); hide(elWon); hide(elJumpback); hide(elOutConfirm);
              if (elLot) elLot.textContent = st.lotNumber ? ('LOT #' + st.lotNumber) : '';
              if (elCurrentWrap) elCurrentWrap.hidden = true;
              if (elLeader) elLeader.hidden = true;
              show(elReggate);
              return;
            }
            hide(elReggate);
            hide(elRegistered);

            // Registered, but no lot is up yet → show a "✓ Registered" badge in the
            // bidding area (in place of the register bar) so the viewer knows they're
            // set and just waiting for the auctioneer.
            if (!st.productId) {
              if (regBadgeEligible()) { showRegisteredBadge(); return; }
              bar.hidden = true; return;
            }
            bar.hidden = false;

            if (elLot) elLot.textContent = st.lotNumber ? ('LOT #' + st.lotNumber) : '';
            // High bidder shows "First L." (name-first), not the screen name (item 1).
            var leaderLabel = '';
            if (st.leaderNum || st.leaderSn) {
              var ld = MHV.chatDisplayName ? MHV.chatDisplayName({
                screenName: st.leaderSn, firstName: st.leaderFirst, lastName: st.leaderLast,
                bidderNumber: st.leaderNum, hideName: st.leaderHide, isAdminView: isAdmin
              }) : { primary: st.leaderSn };
              leaderLabel = ld.primary || st.leaderSn || '';
            }
            if (elLeader) elLeader.textContent = leaderLabel ? ('High Bidder: ' + leaderLabel) : '';
            if (elCurrent) elCurrent.textContent = st.currentBid > 0 ? fmt(st.currentBid) : '—';

            // Waiting for the auctioneer (not open yet, and I'm not the sitting high
            // bidder) → show only the lot; hide the current amount + high bidder.
            var waiting = !st.isOpen && !isHighBidder() && !soldWon;
            if (elCurrentWrap) elCurrentWrap.hidden = waiting;
            if (elLeader) elLeader.hidden = waiting || !leaderLabel;

            // Post-sold: overlay the whole bidding area with who won (item 4). Shown to
            // everyone (winner sees "You won for $X"). Persists until the lot changes or
            // the host reopens bidding.
            if (soldWon) {
              if (elWon) { elWon.textContent = soldWonText; elWon.classList.remove('passed'); }
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed);
              hide(elOverlay); hide(elOutbid); hide(elNoBidder); show(elWon);
              return;
            }
            // Post-pass: overlay the bidding area with "Lot Passed" and hide the bid
            // buttons (item 11). Persists until the lot changes or the host reopens bidding.
            if (lotPassed) {
              if (elWon) { elWon.textContent = '🚫 Lot Passed'; elWon.classList.add('passed'); }
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed);
              hide(elOverlay); hide(elOutbid); hide(elNoBidder); show(elWon);
              return;
            }
            if (elWon) elWon.classList.remove('passed');
            hide(elWon);

            if (!hasBidder) { hide(elActions); hide(elClosed); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elOverlay); hide(elOutbid); show(elNoBidder); return; }
            hide(elNoBidder);

            // Brief red OUTBID! flash in place of the high-bidder badge (item 3), before
            // the bid buttons reappear.
            if (outbidActive && st.isOpen) {
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed); hide(elOverlay); show(elOutbid);
              return;
            }
            hide(elOutbid);

            // Being the high bidder implies I've bid on this lot (covers reconnects where
            // the local bid flag was lost). Gates the OUT button (item 7).
            if (isHighBidder()) iHaveBidThisLot = true;

            // OUT confirm prompt in progress (item 7) → show it, hide everything else.
            if (outConfirming && st.isOpen) {
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed); hide(elOverlay); hide(elJumpback);
              show(elOutConfirm); return;
            }
            hide(elOutConfirm);

            // I declared OUT on this open lot → show the single "Jump back in! Bid $X"
            // button in place of the bid actions (item 7).
            if (iAmOut && st.isOpen && !isHighBidder()) {
              jumpBackAmt = jumpBackAmount();
              if (elJumpAmt) elJumpAmt.textContent = fmt(jumpBackAmt);
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed); hide(elOverlay);
              show(elJumpback); return;
            }
            hide(elJumpback);

            // If I'm the high bidder — even as an absentee before the lot opens — show the
            // overlay and block re-bidding (checked BEFORE the open/closed gate).
            if (isHighBidder()) { hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid); hide(elClosed); show(elOverlay); return; }
            hide(elOverlay);

            // Bidding not open yet. A registered viewer sees the "✓ Registered" badge
            // (in place of the register bar); everyone else sees "Waiting for auctioneer".
            if (!st.isOpen) {
              hide(elActions); hide(elConfirm); hide(elCustom); hide(elChatbid);
              if (regBadgeEligible()) { hide(elClosed); showRegisteredBadge(); }
              else { hide(elRegistered); show(elClosed); }
              return;
            }
            hide(elRegistered);
            hide(elClosed);

            // Preserve an in-progress chat-bid prompt.
            if (elChatbid && !elChatbid.hidden) { hide(elActions); return; }

            // Preserve an in-progress confirm / custom entry.
            if ((elConfirm && !elConfirm.hidden) || (elCustom && !elCustom.hidden)) { hide(elActions); return; }

            var amts = quickAmounts();
            if (elQuickLow) elQuickLow.textContent = 'BID ' + fmt(amts[0]);
            if (elQuickHigh) {
              if (amts[1] > amts[0]) { elQuickHigh.textContent = 'BID ' + fmt(amts[1]); elQuickHigh.hidden = false; }
              else { elQuickHigh.hidden = true; }
            }
            // OUT button on the left — only once I've actually bid on this lot (item 7).
            if (elOut) elOut.hidden = !iHaveBidThisLot;
            show(elActions);
          }

          function enterConfirm(amount) {
            st.pending = amount;
            if (elConfirmAmt) elConfirmAmt.textContent = fmt(amount);
            hide(elActions); hide(elCustom); show(elConfirm);
          }

          // Validate a would-be bid client-side. Returns a HALF-DOLLAR amount (whole or .50,
          // no finer cents), or null (after flashing why). Mirrors the server floor + the
          // "jump back in" half-increment (item 7). Must be >= the min advance over current.
          function validBidOrNull(amount) {
            amount = Math.floor(Number(amount) * 2) / 2;
            if (!(amount > 0)) { flash('Enter a valid amount.', true); return null; }
            var minAdv = minLiveAdvance(st.currentBid);
            if (st.currentBid > 0 && amount < st.currentBid + minAdv) {
              flash('Bid must be at least ' + fmt(minAdv) + ' over the current bid (' + fmt(st.currentBid) + ').', true);
              return null;
            }
            return amount;
          }

          function submit(amount) {
            if (submitting) return;
            // Runtime ban: a bidder banned DURING the show must not be able to keep
            // bidding through the bar. lockSelfOut() sets MHV_IS_BANNED on you-are-banned
            // / admin-user-banned. The server also rejects, but block client-side too.
            if (window.MHV_IS_BANNED) { flash('You are not able to bid.', true); return; }
            var key = window.MHV_CUSTOMER_KEY;
            if (!key) { flash('Please sign in again to bid.', true); return; }
            submitting = true;
            // Failsafe: if the server never acks (dropped socket / lost packet), don't
            // wedge the bar in a permanent "submitting" state — clear it so the bidder
            // can retry. Cleared on place-live-bid-ok/-error (both call resetToQuick).
            if (submitTimer) clearTimeout(submitTimer);
            submitTimer = setTimeout(function () {
              submitTimer = null;
              if (!submitting) return;
              submitting = false;
              resetToQuick();
              flash('Bid timed out — please try again.', true);
              render();
            }, 7000);
            socket.emit('place-live-bid', { productId: st.productId, bidAmount: amount, customerKey: key });
          }

          // A quick button / custom entry routes THROUGH the confirm step.
          function tryConfirm(amount) {
            var v = validBidOrNull(amount);
            if (v != null) enterConfirm(v);
          }

          if (elQuickLow) elQuickLow.addEventListener('click', function () { tryConfirm(quickAmounts()[0]); });
          if (elQuickHigh) elQuickHigh.addEventListener('click', function () { tryConfirm(quickAmounts()[1]); });

          // ---- OUT + Jump-back-in (item 7) ----
          if (elOut) elOut.addEventListener('click', function () { outConfirming = true; render(); });
          if (elOutNo) elOutNo.addEventListener('click', function () { outConfirming = false; render(); });
          if (elOutYes) elOutYes.addEventListener('click', function () {
            var key = window.MHV_CUSTOMER_KEY;
            outConfirming = false;
            if (key && st.productId) socket.emit('announce-out', { productId: st.productId, customerKey: key });
            iAmOut = true;   // switch to the "Jump back in" button
            render();
          });
          if (elJumpbackBtn) elJumpbackBtn.addEventListener('click', function () {
            // Re-enter bidding: clear OUT and route the shown amount through the confirm step.
            iAmOut = false;
            tryConfirm(jumpBackAmt || jumpBackAmount());
          });
          if (elConfirmYes) elConfirmYes.addEventListener('click', function () { submit(st.pending); });
          if (elConfirmCancel) elConfirmCancel.addEventListener('click', function () { resetToQuick(); render(); });
          if (elCustomToggle) elCustomToggle.addEventListener('click', function () {
            hide(elActions); hide(elConfirm); show(elCustom);
            if (elCustomInput) { elCustomInput.value = ''; elCustomInput.focus(); }
          });
          if (elCustomCancel) elCustomCancel.addEventListener('click', function () { resetToQuick(); render(); });
          // Custom "Bid" requires confirmation — it goes to the Confirm $X step, not straight through.
          if (elCustomConfirm) elCustomConfirm.addEventListener('click', function () { tryConfirm(parseFloat(elCustomInput && elCustomInput.value)); });
          if (elCustomInput) elCustomInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); tryConfirm(parseFloat(elCustomInput.value)); }
          });

          // ---- Smart bid detection (item 4) ----
          // Parse a chat message that looks like a bid → the dollar amount, else null.
          // Handles: "25", "$25", "25.50", "bid 25", "b 25", "give 25", "ill give you 25",
          // "i'll bid 25", "sure 25", "yes 25", "ok 25", "go 25", "raise 25", "make it 25".
          function parseBidText(text) {
            if (!text) return null;
            var t = String(text).trim().toLowerCase().replace(/\s+/g, ' ');
            // Half-dollar bids (whole or .50, item 7) — floor any typed decimal to $0.50.
            var half = function (v) { return Math.floor(parseFloat(v) * 2) / 2; };
            var pure = t.match(/^\$?\s*(\d{1,6}(?:\.\d{1,2})?)\s*(?:dollars|bucks)?\.?$/);
            if (pure) return half(pure[1]);
            var phrase = t.match(/^(?:bid|b|give(?: you)?|i(?:'|)ll (?:give(?: you)?|bid|do|go)|sure|yes|yeah|yep|ok(?:ay)?|go|raise|make it|how about)\s+\$?\s*(\d{1,6}(?:\.\d{1,2})?)\b/);
            if (phrase) return half(phrase[1]);
            return null;
          }
          function isValidBidSilent(amount) {
            amount = Number(amount);
            if (!(amount > 0)) return false;
            if (st.currentBid > 0 && amount < st.currentBid + minLiveAdvance(st.currentBid)) return false;
            return true;
          }
          function hideChatbid() {
            if (elChatbid) elChatbid.hidden = true;
            chatbidPending = null; chatbidDecline = null;
          }
          function showChatbid(amount, onDecline) {
            chatbidPending = Math.floor(Number(amount));
            chatbidDecline = onDecline || null;
            if (elChatbidAmt) elChatbidAmt.textContent = fmt(chatbidPending);
            hide(elActions); hide(elConfirm); hide(elCustom);
            if (elChatbid) elChatbid.hidden = false;
          }
          if (elChatbidYes) elChatbidYes.addEventListener('click', function () {
            var a = chatbidPending; hideChatbid();
            if (a != null) submit(a);
            render();
          });
          if (elChatbidNo) elChatbidNo.addEventListener('click', function () {
            var cb = chatbidDecline; hideChatbid();
            if (typeof cb === 'function') cb();
            render();
          });

          // Public hook consumed by the chat send flow: if a typed message looks like a
          // bid AND bidding is live/open for me, prompt "Confirm $X bid?" instead of
          // sending it as a chat message. Returns true if it intercepted the message.
          window.MHV_LIVEBAR = {
            maybePromptBid: function (message, sendAsChat) {
              // OLD bidding mode or after a SOLD → chat numbers go through as plain chat.
              if (!barEnabled) return false;
              if (!(hasBidder && st.productId && st.isOpen) || isHighBidder() || soldWon) return false;
              var amt = parseBidText(message);
              if (amt == null || !isValidBidSilent(amt)) return false;
              showChatbid(amt, sendAsChat);
              return true;
            }
          };

          // Item 4: when the host SOLD the running lot, overlay the bidding area with who
          // won — "You won for $X" for the winner, else "<name> won for $X".
          socket.on('sold-pass-announcement', function (d) {
            if (!d || !st.productId) return;
            if (d.type === 'sold') {
              var iWon = hasBidder && myBidder && String(st.leaderNum) === myBidder;
              var amt = st.currentBid > 0 ? fmt(st.currentBid) : '';
              var who = st.leaderSn || 'Winning bidder';
              soldWonText = iWon
                ? ('🎉 You won' + (amt ? ' for ' + amt : '') + '!')
                : (who + ' won' + (amt ? ' for ' + amt : ''));
              soldWon = true;
              soldWonAt = Date.now();
              lotPassed = false;
              hideChatbid(); resetToQuick();
              render();
            } else if (d.type === 'pass') {
              // Item 11: host passed the lot → hide the bid buttons + show "Lot Passed".
              // Cleared when the lot changes or the host reopens bidding.
              lotPassed = true;
              lotPassedAt = Date.now();
              soldWon = false;
              hideChatbid(); resetToQuick();
              render();
            }
          });

          // "Bidding: old/new" toggle flipped by the host — show/hide the whole bar live.
          socket.on('live-bid-ui-changed', function (d) {
            barEnabled = !!(d && d.enabled);
            render();
          });

          // Authoritative bid state + open/closed gate for the lot on the block.
          socket.on('live-bid-state', function (d) {
            if (!d) return;
            if (typeof d.barEnabled !== 'undefined') barEnabled = d.barEnabled !== false;
            if (!d.productId) { st.productId = null; render(); return; }
            var wasMine = isHighBidder();
            var lotChanged = String(d.productId) !== st.productId;
            st.productId = String(d.productId);
            st.currentBid = Number(d.currentBid || 0);
            st.minimumBid = Number(d.minimumBid || 0);
            st.ask = Number(d.ask || 0);
            st.leaderSn = d.highBidderSn || '';
            st.leaderNum = d.highBidderBidderNumber || '';
            st.leaderFirst = d.highBidderFirstName || '';
            st.leaderLast = d.highBidderLastName || '';
            st.leaderHide = !!d.highBidderHideName;
            st.isOpen = !!d.isOpen;
            // Pad the lot number to 3 digits for display (LOT #001).
            st.lotNumber = (d.lotNumber != null && String(d.lotNumber) !== '')
              ? String(d.lotNumber).replace(/[^0-9]/g, '').padStart(3, '0') : '';
            if (lotChanged) {
              resetToQuick(); hideChatbid();
              outbidActive = false; if (outbidTimer) { clearTimeout(outbidTimer); outbidTimer = null; }
              soldWon = false;
              lotPassed = false;
              iHaveBidThisLot = false; iAmOut = false; outConfirming = false;   // reset OUT state (item 7)
            } else if (soldWon && st.isOpen && (Date.now() - soldWonAt > 1500)) {
              // Host reopened bidding on the same lot → drop the "won" overlay.
              soldWon = false;
            } else if (lotPassed && st.isOpen && (Date.now() - lotPassedAt > 1500)) {
              // Host reopened bidding on a passed lot (item 11) → drop the "passed" overlay.
              lotPassed = false;
            }
            // Item 3: I was leading, a new bid took the lead on an open lot → OUTBID flash.
            if (!lotChanged && wasMine && !isHighBidder() && st.isOpen && !soldWon) {
              triggerOutbidFlash();
              return;
            }
            render();
          });

          socket.on('place-live-bid-ok', function () {
            submitting = false;
            resetToQuick();
            iHaveBidThisLot = true;   // I've now bid on this lot → the OUT button is eligible
            iAmOut = false;           // a successful bid means I jumped back in (item 7)
            outConfirming = false;
            // No success message: it just restated the badge/button and jumped the layout.
            render();
          });
          socket.on('place-live-bid-error', function (d) {
            submitting = false;
            resetToQuick();
            // The server rejected the bid because this viewer isn't registered for
            // the auction — open the in-page registration modal instead of an error.
            if (d && d.code === 'AUCTION_REGISTRATION_REQUIRED') {
              reg.enforced = true; reg.registered = false;
              if (d.auctionCode) reg.auctionCode = d.auctionCode;
              render();
              openRegModal();
              return;
            }
            flash((d && d.message) || 'Bid failed. Try again.', true);
            render();
          });

          // Registration gate wiring — button opens the in-page modal; real-time
          // unlock when the viewer registers or an admin approves them.
          if (elReggateBtn) elReggateBtn.addEventListener('click', openRegModal);
          socket.on('registration-approved', function (d) {
            reg.registered = true;
            if (d && d.auctionCode) reg.auctionCode = d.auctionCode;
            render();
          });
          // The locked-session code is the authoritative auction id (matches the
          // server gate) — adopt it and (re)check registration status.
          socket.on('locked-session', function (d) {
            if (d && d.code && d.code !== reg.auctionCode) { reg.auctionCode = d.code; }
            refreshRegStatus();
          });
          refreshRegStatus();

          // Fast-hide on an explicit lot clear; otherwise a real lot appearing means
          // we should (re)sync — covers late joiners who missed the broadcast.
          socket.on('current-lot-updated', function (d) {
            if (!d) return;
            if (d.sku === 'CLEARED' && d.action === 'clear') { st.productId = null; soldWon = false; lotPassed = false; outbidActive = false; render(); return; }
            if (d.product && (d.product.productId || d.product.id)) socket.emit('request-live-bid-state', {});
          });

          // Ask for the current lot's bid state on (re)connect, in case one is already live.
          socket.on('connect', function () { socket.emit('request-live-bid-state', {}); });
          if (socket.connected) socket.emit('request-live-bid-state', {});
        })();

        // ==========================================================================
        // BIN (BUY-IT-NOW) ANNOUNCEMENTS
        // ==========================================================================
        
        // Track BIN items for purchase state
        window.binPurchaseState = {};
        
        // Listen for BIN item announcements
        socket.on('bin-announcement', function(data) {
          if (!messagesContainer) return;
          
          // Check scroll position BEFORE adding content
          var wasNearBottom = isNearBottom();
          
          // Render the BIN announcement
          renderBinAnnouncement(data);
          
          // Auto-scroll if user was near bottom
          if (wasNearBottom) {
            scrollToBottom();
          }
          
          // Add to BIN list cache and update button visibility
          if (typeof window.updateBinListItems === 'function' && window.binListItems) {
            // Add new item to cache if not already present
            // Note: data.binId is the actual BIN item ID, data.id is the chat message ID
            var binItemId = data.binId || data.id;
            var existingIndex = window.binListItems.findIndex(function(i) { return i.id === binItemId; });
            if (existingIndex === -1) {
              window.binListItems.unshift({
                id: binItemId,
                title: data.title,
                price: data.price,
                status: 'active'
              });
            }
            window.updateBinListItems(window.binListItems);
          }
        });
        
        // Listen for BIN item visibility changes
        socket.on('bin-item-visibility', function(data) {
          var binEl = document.querySelector('[data-bin-id="' + data.id + '"]');
          if (binEl) {
            binEl.classList.toggle('hidden', data.hidden);
          }
        });
        
        // Listen for BIN state to update sold/passed items in chat (after history load)
        socket.on('bin-state', function(data) {
          if (!data.items) return;
          
          data.items.forEach(function(item) {
            var binEl = document.querySelector('[data-bin-id="' + item.id + '"]');
            if (!binEl) return;
            
            if (item.status === 'sold' && !binEl.classList.contains('sold')) {
              // 2026-09-14: the room copy of a BIN sale carries no customer id — the buyer is
              // named by `buyerKey`, the same keyed stand-in chat uses (mySenderKey). The
              // customerId comparison stays for the staff copy, which still carries it.
              var isSoldToMe = !!(item.buyer && ((item.buyer.buyerKey && mySenderKey && item.buyer.buyerKey === mySenderKey) || (item.buyer.customerId && item.buyer.customerId === customerId)));
              
              binEl.classList.add('sold');
              binEl.classList.add(isSoldToMe ? 'sold-to-me' : 'sold-other');
              
              var btnEl = binEl.querySelector('.bin-purchase-btn');
              if (btnEl) {
                btnEl.classList.add('sold');
                btnEl.textContent = 'SOLD';
                btnEl.disabled = true;
              }
              
              // Remove instant notice if present
              var instantNotice = binEl.querySelector('.bin-instant-notice');
              if (instantNotice) instantNotice.remove();
              
              // Add sold info
              var soldInfoEl = binEl.querySelector('.bin-sold-info');
              if (!soldInfoEl) {
                soldInfoEl = document.createElement('div');
                soldInfoEl.className = 'bin-sold-info';
                binEl.appendChild(soldInfoEl);
              }
              
              if (isSoldToMe) {
                soldInfoEl.textContent = 'You purchased this item!';
              } else if (item.buyer) {
                soldInfoEl.textContent = 'Sold to ' + item.buyer.screenName + ' #' + item.buyer.bidderNumber;
              }
            } else if (item.status === 'passed' && !binEl.classList.contains('passed')) {
              binEl.classList.add('passed');
              
              var btnEl = binEl.querySelector('.bin-purchase-btn');
              if (btnEl) {
                btnEl.classList.add('sold');
                btnEl.textContent = 'PASSED';
                btnEl.disabled = true;
              }
              
              // Remove instant notice if present
              var instantNotice = binEl.querySelector('.bin-instant-notice');
              if (instantNotice) instantNotice.remove();
            }
          });
          
          // Update BIN list button/lightbox with active items
          if (typeof window.updateBinListItems === 'function') {
            window.updateBinListItems(data.items || data.activeItems || []);
          }
        });
        
        // Listen for BIN item updates (title/price changes)
        socket.on('bin-item-chat-updated', function(data) {
          var binEl = document.querySelector('[data-bin-id="' + data.id + '"]');
          if (binEl) {
            var titleEl = binEl.querySelector('.bin-announcement-title');
            var priceEl = binEl.querySelector('.bin-announcement-price');
            var btnEl = binEl.querySelector('.bin-purchase-btn');
            
            if (titleEl) titleEl.textContent = data.title;
            if (priceEl) priceEl.textContent = '$' + data.price.toFixed(2);
            if (btnEl && !btnEl.classList.contains('sold') && !btnEl.classList.contains('confirming')) {
              btnEl.textContent = 'BIN FOR $' + data.price.toFixed(2);
              btnEl.dataset.price = data.price;
            }
          }
          
          // Update in BIN list cache
          if (window.binListItems) {
            var itemIndex = window.binListItems.findIndex(function(i) { return i.id === data.id; });
            if (itemIndex !== -1) {
              window.binListItems[itemIndex].title = data.title;
              window.binListItems[itemIndex].price = data.price;
              if (typeof window.updateBinListItems === 'function') {
                window.updateBinListItems(window.binListItems);
              }
            }
          }
        });
        
        // Listen for BIN item moved to bottom of chat (after edit)
        // This removes the old announcement and adds a new one at the bottom
        socket.on('bin-item-chat-moved', function(data) {
          if (!messagesContainer) return;
          
          // Remove any existing BIN announcement with this ID
          // Try multiple ways to find it (handles different ID formats)
          var oldBinId = data.oldId;
          var oldBinEl = document.querySelector('[data-bin-id="' + oldBinId + '"]');
          if (!oldBinEl) {
            // Try with bin-chat- prefix
            oldBinEl = document.querySelector('[data-bin-id="bin-chat-' + oldBinId + '"]');
          }
          if (!oldBinEl) {
            // Try removing bin-chat- prefix
            var strippedId = oldBinId.replace('bin-chat-', '');
            oldBinEl = document.querySelector('[data-bin-id="' + strippedId + '"]');
          }
          
          if (oldBinEl) {
            oldBinEl.remove();
            console.log('🗑️ Removed old BIN announcement from chat:', oldBinId);
          }
          
          // Render the new BIN announcement at the bottom
          var wasNearBottom = isNearBottom();
          renderBinAnnouncement(data.newMessage);
          console.log('📝 Added updated BIN announcement at bottom:', data.newMessage.title);
          
          // Scroll to bottom if user was already near bottom
          if (wasNearBottom) {
            scrollToBottom();
          }
          
          // Update in BIN list cache with new data
          if (window.binListItems) {
            var itemIndex = window.binListItems.findIndex(function(i) { return i.id === oldBinId; });
            if (itemIndex !== -1) {
              window.binListItems[itemIndex].title = data.newMessage.title;
              window.binListItems[itemIndex].price = data.newMessage.price;
              window.binListItems[itemIndex].consignor = data.newMessage.consignor;
              window.binListItems[itemIndex].lot = data.newMessage.lot;
              if (typeof window.updateBinListItems === 'function') {
                window.updateBinListItems(window.binListItems);
              }
            }
          }
        });
        
        // Listen for BIN item deletion
        socket.on('bin-item-chat-deleted', function(data) {
          var binEl = document.querySelector('[data-bin-id="' + data.id + '"]');
          if (binEl) {
            binEl.remove();
          }
          
          // Remove from BIN list
          if (typeof window.removeBinListItem === 'function') {
            window.removeBinListItem(data.id);
          }
        });
        
        // Listen for BIN item status change (e.g., passed)
        socket.on('bin-item-status-changed', function(data) {
          var binEl = document.querySelector('[data-bin-id="' + data.id + '"]');
          if (binEl) {
            if (data.status === 'passed') {
              binEl.classList.add('passed');
              
              var btnEl = binEl.querySelector('.bin-purchase-btn');
              if (btnEl) {
                btnEl.classList.add('sold');
                btnEl.textContent = 'PASSED';
                btnEl.disabled = true;
              }
              
              // Remove instant notice if present
              var instantNotice = binEl.querySelector('.bin-instant-notice');
              if (instantNotice) instantNotice.remove();
            }
          }
          
          // Remove from BIN list if sold or passed
          if (data.status === 'sold' || data.status === 'passed') {
            if (typeof window.removeBinListItem === 'function') {
              window.removeBinListItem(data.id);
            }
          }
        });
        
        // Listen for BIN confirm request (after clicking BIN button)
        socket.on('bin-confirm-request', function(data) {
          var btnEl = document.querySelector('[data-bin-id="' + data.binId + '"] .bin-purchase-btn');
          if (btnEl) {
            btnEl.classList.add('confirming');
            btnEl.textContent = 'CONFIRM $' + data.price.toFixed(2) + ' PURCHASE';
            btnEl.dataset.confirming = 'true';
          }
          
          // Also update BIN list button if visible
          if (typeof window.updateBinListItemState === 'function') {
            window.updateBinListItemState(data.binId, 'confirming');
          }
        });
        
        // Listen for BIN purchase errors
        socket.on('bin-purchase-error', function(data) {
          if (typeof window.showError === 'function') {
            window.showError(data.message);
          } else {
            MHV.toast(data.message, { type: 'error' });
          }
          
          // Reset any confirming or disabled buttons (not sold ones)
          var allBinBtns = document.querySelectorAll('.bin-purchase-btn:not(.sold)');
          allBinBtns.forEach(function(btn) {
            var price = parseFloat(btn.dataset.price) || 0;
            btn.classList.remove('confirming');
            btn.textContent = 'BIN FOR $' + price.toFixed(2);
            btn.dataset.confirming = 'false';
            btn.disabled = false;
          });
          
          // Also reset BIN list buttons
          var allBinListBtns = document.querySelectorAll('.bin-list-purchase-btn:not(.sold)');
          allBinListBtns.forEach(function(btn) {
            var price = parseFloat(btn.dataset.price) || 0;
            var binId = btn.dataset.binId;
            btn.classList.remove('confirming', 'loading');
            btn.textContent = 'BIN $' + price.toFixed(2);
            btn.dataset.confirming = 'false';
            btn.disabled = false;
            // Clear any timeouts
            if (window.binListConfirmTimeouts && window.binListConfirmTimeouts[binId]) {
              clearTimeout(window.binListConfirmTimeouts[binId]);
              delete window.binListConfirmTimeouts[binId];
            }
          });
        });
        
        // Listen for BIN item sold
        socket.on('bin-item-sold', function(data) {
          // Try multiple approaches to find the BIN element
          var binEl = document.querySelector('.custom-chat-bin-announcement[data-bin-id="' + data.id + '"]');
          
          // Fallback: try to find by the button's data-bin-id attribute
          if (!binEl) {
            var btnByBinId = document.querySelector('.bin-purchase-btn[data-bin-id="' + data.id + '"]');
            if (btnByBinId) {
              binEl = btnByBinId.closest('.custom-chat-bin-announcement');
            }
          }
          
          // Another fallback: try all BIN announcements and check for matching button or data-bin-id
          if (!binEl) {
            var allBinAnnouncements = document.querySelectorAll('.custom-chat-bin-announcement');
            for (var i = 0; i < allBinAnnouncements.length; i++) {
              var announcement = allBinAnnouncements[i];
              var announcementBinId = announcement.dataset.binId;
              var btn = announcement.querySelector('.bin-purchase-btn[data-bin-id="' + data.id + '"]');
              if (btn || announcementBinId === data.id) {
                binEl = announcement;
                break;
              }
            }
          }
          
          // Last resort: try partial match with normalized IDs
          if (!binEl) {
            var allBinAnnouncements = document.querySelectorAll('.custom-chat-bin-announcement:not(.sold)');
            for (var i = 0; i < allBinAnnouncements.length; i++) {
              var announcement = allBinAnnouncements[i];
              var announcementBinId = announcement.dataset.binId;
              var normalizedDataId = data.id.replace('bin-chat-', '').replace('bin-', '');
              var normalizedAnnouncementId = announcementBinId ? announcementBinId.replace('bin-chat-', '').replace('bin-', '') : '';
              if (normalizedDataId && normalizedAnnouncementId && normalizedDataId === normalizedAnnouncementId) {
                binEl = announcement;
                break;
              }
            }
          }
          
          var isBuyer = !!(data.buyer && ((data.buyer.buyerKey && mySenderKey && data.buyer.buyerKey === mySenderKey) || (data.buyer.customerId && data.buyer.customerId === customerId)));
          
          if (binEl) {
            binEl.classList.add('sold');
            
            // Add class based on whether viewer is the buyer
            if (isBuyer) {
              binEl.classList.add('sold-to-me');
            } else {
              binEl.classList.add('sold-other');
            }
            
            var btnEl = binEl.querySelector('.bin-purchase-btn');
            if (btnEl) {
              btnEl.classList.remove('confirming');
              btnEl.classList.add('sold');
              btnEl.textContent = 'SOLD';
              btnEl.disabled = true;
              // Clear any timeout
              if (btnEl.confirmTimeout) {
                clearTimeout(btnEl.confirmTimeout);
                btnEl.confirmTimeout = null;
              }
            }
            
            // Remove instant notice if present
            var instantNotice = binEl.querySelector('.bin-instant-notice');
            if (instantNotice) instantNotice.remove();
            
            // Add sold info
            var soldInfoEl = binEl.querySelector('.bin-sold-info');
            if (!soldInfoEl) {
              soldInfoEl = document.createElement('div');
              soldInfoEl.className = 'bin-sold-info';
              binEl.appendChild(soldInfoEl);
            }
            
            if (isBuyer) {
              soldInfoEl.textContent = 'You purchased this item!';
            } else {
              soldInfoEl.textContent = 'Sold to ' + data.buyer.screenName + ' #' + data.buyer.bidderNumber;
            }
          }
          
          // Show celebration animation only to the buyer
          if (isBuyer) {
            createSoldCelebration();
          }
          
          // Update BIN list button state to sold before removing
          if (typeof window.updateBinListItemState === 'function') {
            window.updateBinListItemState(data.id, 'sold');
          }
          
          // Remove from BIN list after a brief delay to show sold state
          setTimeout(function() {
            if (typeof window.removeBinListItem === 'function') {
              window.removeBinListItem(data.id);
            }
          }, 1500);
        });
        
        // Listen for BIN sold announcement - DISABLED: now showing sold info in the original BIN card
        // socket.on('bin-sold-announcement', function(data) {
        //   if (!messagesContainer) return;
        //   var wasNearBottom = isNearBottom();
        //   renderBinSoldAnnouncement(data);
        //   if (wasNearBottom) {
        //     scrollToBottom();
        //   }
        // });
        
        // Render BIN announcement in chat
        function renderBinAnnouncement(data) {
          if (!messagesContainer) return;
          
          var uniqueId = data.id || ('bin-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9));
          // Get the actual BIN item ID - use binId if available, otherwise extract from the chat message id
          // or use the id directly if it looks like a BIN item ID
          var actualBinId = data.binId || (data.id && data.id.startsWith('bin-chat-') ? data.id.replace('bin-chat-', '') : data.id);
          var isPurchaseImmediate = getBinPurchaseSetting() === 'immediate';
          var isSold = data.status === 'sold';
          var soldBuyer = data.buyer;
          var isSoldToMe = !!(isSold && soldBuyer && ((soldBuyer.buyerKey && mySenderKey && soldBuyer.buyerKey === mySenderKey) || (soldBuyer.customerId && soldBuyer.customerId === customerId)));
          
          // Build classes for the container
          var containerClasses = 'custom-chat-bin-announcement';
          if (isSold) {
            containerClasses += ' sold';
            containerClasses += isSoldToMe ? ' sold-to-me' : ' sold-other';
          }
          
          var buyerKeyAttr = (isSold && soldBuyer && soldBuyer.buyerKey) ? ' data-buyer-key="' + MHV.escapeAttr(String(soldBuyer.buyerKey)) + '"' : '';
          var html = '<div class="' + containerClasses + '" id="' + uniqueId + '" data-bin-id="' + actualBinId + '" data-message-id="' + uniqueId + '"' + buyerKeyAttr + '>';
          
          // Add admin delete button
          if (isAdmin) {
            html += '<div class="bin-admin-btns">';
            html += '<button class="bin-delete-btn" data-message-id="' + uniqueId + '" title="Delete">✕</button>';
            html += '</div>';
          }
          
          html += '<span class="bin-announcement-label">Buy-It-Now</span>';
          html += '<span class="bin-announcement-title">' + MHV.escapeHtml(data.title) + '</span>';
          
          // Handle sold vs active items
          if (isSold) {
            // Show SOLD button
            html += '<button class="bin-purchase-btn sold" disabled>SOLD</button>';
            
            // Show sold info
            if (isSoldToMe) {
              html += '<div class="bin-sold-info">You purchased this item!</div>';
            } else if (soldBuyer) {
              html += '<div class="bin-sold-info">Sold to ' + MHV.escapeHtml(soldBuyer.screenName) + ' #' + MHV.escapeHtml(soldBuyer.bidderNumber) + '</div>';
            }
          } else {
            // Active item - show purchase button
            if (isLoggedIn) {
              html += '<button class="bin-purchase-btn" data-bin-id="' + actualBinId + '" data-price="' + data.price + '" data-confirming="false">';
              html += 'BIN FOR $' + data.price.toFixed(2);
              html += '</button>';
              
              // Show "Purchase Immediately" notice if that setting is on
              if (isPurchaseImmediate) {
                html += '<div class="bin-instant-notice">';
                html += '<span>Purchase Immediately On</span>';
                html += '<button class="bin-turn-off-btn" data-bin-id="' + actualBinId + '">Turn Off</button>';
                html += '</div>';
              }
            } else {
              html += '<button class="bin-purchase-btn" disabled>Log in to purchase</button>';
            }
          }
          
          html += '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
          
          // Attach event handlers
          var binEl = document.getElementById(uniqueId);
          if (binEl) {
            // Admin delete handler
            if (isAdmin) {
              var deleteBtn = binEl.querySelector('.bin-delete-btn');
              if (deleteBtn) {
                deleteBtn.addEventListener('click', function(e) {
                  e.stopPropagation();
                  socket.emit('admin-delete-message', { messageId: uniqueId });
                });
              }
            }
            
            // Turn off instant purchase button handler
            var turnOffBtn = binEl.querySelector('.bin-turn-off-btn');
            if (turnOffBtn) {
              turnOffBtn.addEventListener('click', function(e) {
                e.stopPropagation();
                setBinPurchaseSetting('confirm');
                // Remove all instant notices
                document.querySelectorAll('.bin-instant-notice').forEach(function(el) { el.remove(); });
              });
            }
            
            // Purchase button handler
            var purchaseBtn = binEl.querySelector('.bin-purchase-btn');
            if (purchaseBtn && isLoggedIn) {
              purchaseBtn.addEventListener('click', function(e) {
                e.stopPropagation();
                
                if (this.classList.contains('sold') || this.disabled) {
                  return;
                }
                
                var btn = this;
                var binId = btn.dataset.binId;
                var price = parseFloat(btn.dataset.price) || 0;
                var isConfirming = btn.dataset.confirming === 'true';
                var isPurchaseImmediateNow = getBinPurchaseSetting() === 'immediate';
                
                if (isConfirming) {
                  // Second click - confirm purchase
                  btn.innerHTML = '<span class="loading-ellipsis">Claiming</span>';
                  btn.disabled = true;
                  // Clear any timeout
                  if (btn.confirmTimeout) {
                    clearTimeout(btn.confirmTimeout);
                    btn.confirmTimeout = null;
                  }
                  socket.emit('bin-confirm-purchase', { binId: binId });
                } else if (isPurchaseImmediateNow) {
                  // Purchase Immediately mode - skip confirmation, submit directly
                  btn.innerHTML = '<span class="loading-ellipsis">Claiming</span>';
                  btn.disabled = true;
                  // Direct confirmation - no delay
                  socket.emit('bin-confirm-purchase', { binId: binId });
                } else {
                  // First click - request confirmation
                  socket.emit('bin-purchase', { binId: binId });
                  
                  // Set 5-second timeout to reset button
                  btn.confirmTimeout = setTimeout(function() {
                    if (btn.dataset.confirming === 'true' && !btn.classList.contains('sold')) {
                      btn.classList.remove('confirming');
                      btn.textContent = 'BIN FOR $' + price.toFixed(2);
                      btn.dataset.confirming = 'false';
                    }
                  }, 5000);
                }
              });
            }
          }
        }
        
        // Get BIN purchase setting
        function getBinPurchaseSetting() {
          try {
            return localStorage.getItem('mhv_bin_purchase_mode') || 'confirm';
          } catch (e) {
            return 'confirm';
          }
        }
        
        // Set BIN purchase setting
        function setBinPurchaseSetting(mode) {
          try {
            if (mode === 'confirm') {
              localStorage.removeItem('mhv_bin_purchase_mode');
            } else {
              localStorage.setItem('mhv_bin_purchase_mode', mode);
            }
            // Update settings UI if open
            var select = document.getElementById('user-bin-purchase-mode');
            if (select) select.value = mode;
          } catch (e) {
            console.warn('Error saving BIN purchase setting:', e);
          }
        }
        
        // Render BIN sold announcement in chat
        function renderBinSoldAnnouncement(data) {
          if (!messagesContainer) return;
          
          var uniqueId = data.id || ('bin-sold-' + Date.now());
          
          var html = '<div class="custom-chat-bin-sold" id="' + uniqueId + '" data-message-id="' + uniqueId + '">';
          html += '<span class="bin-sold-label">🎉 BIN SOLD! 🎉</span>';
          html += '<span class="bin-sold-title">' + MHV.escapeHtml(data.title) + ' - $' + data.price.toFixed(2) + '</span>';
          html += '<span class="bin-sold-buyer">' + MHV.escapeHtml(data.buyer.screenName) + ' #' + data.buyer.bidderNumber + '</span>';
          html += '</div>';
          
          messagesContainer.insertAdjacentHTML('beforeend', html);
        }
        
        // Listen for sold celebration animation broadcast
        socket.on('sold-celebration', function(data) {
          createSoldCelebration();
        });

        // Listen for giveaway winner broadcast — add chat announcement + celebration
        socket.on('giveaway-winner-broadcast', function(data) {
          console.log('🎁 Giveaway winner announced in chat:', data.screenName);
          var messagesContainer = document.getElementById('custom-chat-messages');
          if (messagesContainer) {
            var html = '<div class="custom-chat-message giveaway-announcement">';
            html += '<div class="chat-announcement-text">';
            var gwName = (MHV.chatDisplayName
              ? MHV.chatDisplayName({ screenName: data.screenName, firstName: data.firstName, lastName: data.lastName, bidderNumber: data.bidderNumber, hideName: data.hideName, isAdminView: isAdmin }).primary
              : (data.displayName || data.screenName)) || 'Winner';
            html += '🎁 <strong>' + MHV.escapeHtml(gwName) + '</strong> WINS <strong>$' + (data.amount || 0) + ' OFF!</strong> 🎉';
            html += '</div>';
            html += '</div>';
            var wasNearBottom = isNearBottom();
            messagesContainer.insertAdjacentHTML('beforeend', html);
            if (wasNearBottom) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
          }
          createSoldCelebration();
        });

        // Create the colorful dots celebration animation
        function createSoldCelebration() {
          console.log('🎉 Creating sold celebration animation');
          
          var container = document.createElement('div');
          container.className = 'sold-celebration-container';
          document.body.appendChild(container);
          
          // Center of screen for explosion origin
          var centerX = window.innerWidth / 2;
          var centerY = window.innerHeight / 2;
          
          // Colors for the dots - mostly white with some colorful ones
          var colors = [
            '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff',
            '#ff0066', '#00ff88', '#ffff00', '#00ccff', '#ff9900',
            '#ff00ff', '#00ffcc', '#ff6666', '#66ff66', '#6666ff'
          ];
          
          // Create many dots
          var numDots = 60;
          for (var i = 0; i < numDots; i++) {
            var dot = document.createElement('div');
            dot.className = 'sold-celebration-dot';
            
            // Random color
            var color = colors[Math.floor(Math.random() * colors.length)];
            dot.style.backgroundColor = color;
            
            // Random size (8-16px)
            var size = 8 + Math.random() * 8;
            dot.style.width = size + 'px';
            dot.style.height = size + 'px';
            
            // Start at center
            dot.style.left = centerX + 'px';
            dot.style.top = centerY + 'px';
            
            // Random direction for explosion
            var angle = Math.random() * Math.PI * 2;
            var distance = 150 + Math.random() * 300;
            var dx = Math.cos(angle) * distance;
            var dy = Math.sin(angle) * distance - 100; // Bias upward slightly
            
            // Use CSS transition for movement (applied after a tiny delay)
            dot.style.transition = 'transform 1.5s ease-out';
            dot.style.transform = 'translate(0, 0) scale(1)';
            
            // Store target transform
            dot.dataset.targetX = dx;
            dot.dataset.targetY = dy;
            
            // Stagger the start times slightly
            var delay = Math.random() * 200;
            dot.style.animationDelay = delay + 'ms';
            
            container.appendChild(dot);
            
            // Trigger the movement animation after a tiny delay
            (function(dotEl, targetX, targetY, startDelay) {
              setTimeout(function() {
                dotEl.style.transform = 'translate(' + targetX + 'px, ' + targetY + 'px) scale(0.3)';
              }, startDelay + 10);
            })(dot, dx, dy, delay);
          }
          
          // Remove container after animation completes
          setTimeout(function() {
            if (container.parentNode) {
              container.parentNode.removeChild(container);
            }
          }, 2000);
        }

        // Function to update chat state UI
        function updateChatState(isOpen) {
          var closedNotice = chatContainer.querySelector('#custom-chat-closed-notice');
          var toggleBtn = chatContainer.querySelector('#admin-toggle-chat');
          
          if (isOpen) {
            chatContainer.classList.remove('chat-closed');
            if (closedNotice) closedNotice.classList.remove('visible');
            if (toggleBtn) {
              toggleBtn.classList.add('chat-open');
              toggleBtn.innerHTML = '<span class="admin-btn-emoji">🔓 </span>Chat Open';
              toggleBtn.title = 'Chat is open - click to close';
            }
          } else {
            chatContainer.classList.add('chat-closed');
            if (closedNotice) closedNotice.classList.add('visible');
            if (toggleBtn) {
              toggleBtn.classList.remove('chat-open');
              toggleBtn.innerHTML = '<span class="admin-btn-emoji">🔒 </span>Chat Closed';
              toggleBtn.title = 'Chat is closed - click to open';
            }
          }
        }

        // Listen for chat state changes
        // The viewer's own sender key, pushed by the server once new-user has resolved
        // (it used to ride only on chat-state, which was answered BEFORE new-user
        // finished, so it was empty and no message or BIN sale ever read as mine).
        socket.on('sender-key', function(data) {
          if (data && typeof data.senderKey === 'string' && data.senderKey && data.senderKey !== mySenderKey) {
            mySenderKey = data.senderKey;
            remarkMine();
          }
        });

        socket.on('chat-state', function(data) {
          if (data && typeof data.senderKey === 'string' && data.senderKey && data.senderKey !== mySenderKey) { mySenderKey = data.senderKey; remarkMine(); }
          updateChatState(data.isOpen);
          if (data.gifEnabled !== undefined) updateFeatureState(data.gifEnabled, data.reactionsEnabled);
        });

        socket.on('admin-chat-opened', function() {
          updateChatState(true);
          addNotification('Chat has been opened', 'system');
        });

        socket.on('admin-chat-closed', function() {
          updateChatState(false);
          addNotification('Chat has been closed', 'system');
        });

        // Feature toggle state (gif/reactions)
        socket.on('feature-state', function(data) {
          updateFeatureState(data.gifEnabled, data.reactionsEnabled, data.giphyRateLimited);
        });

        // Live profile update — invalidate client cache so next profile view is fresh
        socket.on('profile-updated', function(data) {
          if (!data || !data.customerId) return;
          // Invalidate profile lightbox cache
          if (window.invalidateProfileCache) invalidateProfileCache(data.customerId);
          // If it's our own update, refresh chat container data attributes
          if (String(data.customerId) === String(customerId)) {
            if (data.profileImage !== undefined) chatContainer.dataset.customerImage = data.profileImage;
            if (data.screenName !== undefined) chatContainer.dataset.customerName = data.screenName;
          }
        });

        function updateFeatureState(gifEnabled, reactionsEnabled, giphyRateLimited) {
          var gifBtn = document.getElementById('chat-gif-btn');
          var reactionBtn = document.getElementById('global-reaction-btn');

          if (gifBtn) {
            if (gifEnabled) {
              gifBtn.classList.remove('feature-disabled');
              gifBtn.onclick = function() { toggleChatGifPanel(); };
            } else {
              gifBtn.classList.add('feature-disabled');
              var msg = giphyRateLimited
                ? 'GIFs temporarily unavailable due to high demand. They\u2019ll be back shortly!'
                : 'GIFs not available at the moment. Try again later!';
              gifBtn.onclick = function() { showGifToast(msg); };
              if (window.closeChatGifPanel) closeChatGifPanel();
            }
          }

          if (reactionBtn) {
            if (reactionsEnabled) {
              reactionBtn.classList.remove('feature-disabled');
            } else {
              reactionBtn.classList.add('feature-disabled');
            }
          }

          // Update admin toggle pills if present
          var gifToggle = document.getElementById('admin-gif-toggle');
          var reactToggle = document.getElementById('admin-reactions-toggle');
          if (gifToggle) {
            gifToggle.checked = gifEnabled;
            // If auto-disabled by rate limit, show visual indicator to admin
            if (giphyRateLimited && !gifEnabled) {
              gifToggle.closest('.admin-feature-pill')?.setAttribute('title', 'GIFs auto-disabled (GIPHY rate limit). Will re-enable automatically.');
            } else {
              gifToggle.closest('.admin-feature-pill')?.setAttribute('title', 'Toggle GIFs for all users');
            }
          }
          if (reactToggle) reactToggle.checked = reactionsEnabled;
        }

        // ==========================================================================
        // REACTION SYSTEM
        // ==========================================================================
        
        // Handle profile view (click avatar or screen name)
        messagesContainer.addEventListener('click', function(e) {
          var profileTarget = e.target.closest('.custom-chat-avatar, .custom-chat-avatar-placeholder, .custom-chat-name');
          if (profileTarget && (window.openProfileLightbox || window.openProfileLightboxByBidder)) {
            var msgEl = e.target.closest('.custom-chat-message[data-profile-key]');
            if (msgEl) {
              e.stopPropagation();
              var preload = {
                screenName: msgEl.dataset.screenName,
                profileImage: msgEl.dataset.profileImage,
                badge: msgEl.dataset.badge,
                bidderNumber: msgEl.dataset.bidderNumber,
              };
              if (msgEl.dataset.customerId && window.openProfileLightbox) {
                openProfileLightbox(msgEl.dataset.customerId, preload);
              } else if (msgEl.dataset.bidderNumber && window.openProfileLightboxByBidder) {
                openProfileLightboxByBidder(msgEl.dataset.bidderNumber, preload);
              }
              return;
            }
          }
        });

        // Handle reaction button click (show picker)
        messagesContainer.addEventListener('click', function(e) {
          var reactionBtn = e.target.closest('.custom-chat-reaction-btn');
          if (reactionBtn) {
            e.stopPropagation();
            var messageId = reactionBtn.dataset.messageId;
            var picker = messagesContainer.querySelector('.custom-chat-reaction-picker[data-message-id="' + messageId + '"]');
            
            // Close all other pickers first
            var allPickers = messagesContainer.querySelectorAll('.custom-chat-reaction-picker.visible');
            allPickers.forEach(function(p) {
              if (p !== picker) p.classList.remove('visible');
            });
            
            // Toggle this picker
            if (picker) {
              picker.classList.toggle('visible');
            }
            return;
          }
          
          // Handle emoji selection from picker
          var emojiBtn = e.target.closest('.custom-chat-reaction-picker button');
          if (emojiBtn) {
            e.stopPropagation();
            var emoji = emojiBtn.dataset.emoji;
            var picker = emojiBtn.closest('.custom-chat-reaction-picker');
            var messageId = picker.dataset.messageId;
            
            if (emoji && messageId && isLoggedIn) {
              socket.emit('add-reaction', {
                messageId: messageId,
                emoji: emoji,
                screenName: customerName
              });
            }
            
            // Close the picker
            picker.classList.remove('visible');
            return;
          }
          
          // Handle clicking on existing reaction badge (to add same reaction)
          var reactionBadge = e.target.closest('.custom-chat-reaction-badge');
          if (reactionBadge && isLoggedIn) {
            e.stopPropagation();
            var emoji = reactionBadge.dataset.emoji;
            var reactionsDiv = reactionBadge.closest('.custom-chat-reactions');
            var messageId = reactionsDiv.dataset.messageId;
            
            if (emoji && messageId) {
              socket.emit('add-reaction', {
                messageId: messageId,
                emoji: emoji,
                screenName: customerName
              });
            }
            return;
          }
          
          // Close all pickers when clicking elsewhere
          var allPickers = messagesContainer.querySelectorAll('.custom-chat-reaction-picker.visible');
          allPickers.forEach(function(p) { p.classList.remove('visible'); });
        });
        
        // Close picker when clicking outside messages
        document.addEventListener('click', function(e) {
          if (!e.target.closest('.custom-chat-messages')) {
            var allPickers = messagesContainer.querySelectorAll('.custom-chat-reaction-picker.visible');
            allPickers.forEach(function(p) { p.classList.remove('visible'); });
          }
        });
        
        // Handle reaction updates from server
        socket.on('reaction-updated', function(data) {
          // Check if user is near bottom before updating
          var wasNearBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight < 100;
          
          var messageEl = messagesContainer.querySelector('.custom-chat-message[data-message-id="' + data.messageId + '"]');
          if (messageEl) {
            var reactionsDiv = messageEl.querySelector('.custom-chat-reactions');
            if (reactionsDiv) {
              reactionsDiv.outerHTML = buildReactionsHtml(data.reactions, data.messageId);
            }
            
            // Auto-scroll if user was near bottom
            if (wasNearBottom) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
          }
        });
        
        // Helper to build reactions HTML (needs to be accessible here)
        function buildReactionsHtml(reactions, messageId) {
          if (!reactions || Object.keys(reactions).length === 0) {
            return '<div class="custom-chat-reactions" data-message-id="' + MHV.escapeAttr(messageId || '') + '"></div>';
          }

          var html = '<div class="custom-chat-reactions" data-message-id="' + MHV.escapeAttr(messageId || '') + '">';
          for (var emoji in reactions) {
            if (reactions.hasOwnProperty(emoji) && reactions[emoji].length > 0) {
              var isUserReacted = reactions[emoji].indexOf(customerName) !== -1;
              html += '<span class="custom-chat-reaction-badge' + (isUserReacted ? ' user-reacted' : '') + '" data-emoji="' + MHV.escapeAttr(emoji) + '" title="' + MHV.escapeAttr(reactions[emoji].join(', ')) + '">' +
                        '<span class="emoji">' + MHV.escapeHtml(emoji) + '</span>' +
                        '<span class="count">' + reactions[emoji].length + '</span>' +
                      '</span>';
            }
          }
          html += '</div>';
          return html;
        }

        // ==========================================================================
        // GLOBAL REACTION SYSTEM (Popping reactions)
        // ==========================================================================
        
        var globalReactionBtn = chatContainer.querySelector('#global-reaction-btn');
        var globalReactionMenu = chatContainer.querySelector('#global-reaction-menu');
        var globalReactionSnoozeBtn = chatContainer.querySelector('#global-reaction-snooze');
        var globalReactionTimeout = null;
        
        // Check if reactions are disabled (stored in localStorage - persists until changed)
        function isReactionsDisabled() {
          // Check new key first, then fall back to old snooze key for backwards compatibility
          var disabled = localStorage.getItem('mhv_reactions_disabled');
          if (disabled === 'true') return true;
          // Legacy: also check old snooze expiry (will be migrated)
          var snoozeExpiry = localStorage.getItem('mhv_reactions_snoozed_until');
          if (snoozeExpiry && Date.now() < parseInt(snoozeExpiry, 10)) return true;
          return false;
        }
        
        // Alias for backwards compatibility
        function isReactionsSnoozed() {
          return isReactionsDisabled();
        }
        
        // Update reaction UI state (syncs both toggles)
        var userReactionsToggle = chatContainer.querySelector('#user-reactions-toggle');
        
        function updateSnoozeState() {
          var disabled = isReactionsDisabled();
          
          // Update reaction menu state
          if (globalReactionMenu) {
            var container = globalReactionMenu.closest('.global-reaction-container');
            if (disabled) {
              globalReactionMenu.classList.add('snoozed');
              if (container) container.classList.add('snoozed');
            } else {
              globalReactionMenu.classList.remove('snoozed');
              if (container) container.classList.remove('snoozed');
            }
          }
          
          // Update settings popup toggle state
          if (userReactionsToggle) {
            if (disabled) {
              userReactionsToggle.classList.add('off');
            } else {
              userReactionsToggle.classList.remove('off');
            }
          }
        }
        
        // Initialize reaction state on load
        updateSnoozeState();
        
        // Handle settings popup reactions toggle
        if (userReactionsToggle) {
          userReactionsToggle.addEventListener('click', function(e) {
            e.stopPropagation();
            
            if (isReactionsDisabled()) {
              // Enable reactions - remove both old and new keys
              localStorage.removeItem('mhv_reactions_disabled');
              localStorage.removeItem('mhv_reactions_snoozed_until');
            } else {
              // Disable reactions (persists until manually enabled)
              localStorage.setItem('mhv_reactions_disabled', 'true');
              localStorage.removeItem('mhv_reactions_snoozed_until'); // Clean up old key
            }
            
            updateSnoozeState();
          });
        }
        
        if (globalReactionBtn && globalReactionMenu) {
          // Toggle menu on button click
          globalReactionBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            if (globalReactionBtn.classList.contains('feature-disabled')) {
              showGifToast('Reactions not available at the moment. Try again later!');
              return;
            }
            var isOpen = globalReactionMenu.classList.contains('visible');
            
            if (isOpen) {
              closeGlobalReactionMenu();
            } else {
              openGlobalReactionMenu();
            }
          });
          
          function openGlobalReactionMenu() {
            globalReactionMenu.classList.add('visible');
            resetMenuTimeout();
          }
          
          function closeGlobalReactionMenu() {
            globalReactionMenu.classList.remove('visible');
            if (globalReactionTimeout) {
              clearTimeout(globalReactionTimeout);
              globalReactionTimeout = null;
            }
          }
          
          function resetMenuTimeout() {
            if (globalReactionTimeout) {
              clearTimeout(globalReactionTimeout);
            }
            globalReactionTimeout = setTimeout(function() {
              closeGlobalReactionMenu();
            }, 5000);
          }
          
          // Handle reaction option clicks
          globalReactionMenu.addEventListener('click', function(e) {
            var option = e.target.closest('.global-reaction-option');
            if (option) {
              e.stopPropagation();
              var reactionType = option.dataset.reaction;
              var imgUrl = option.dataset.img;
              
              // Send the reaction to all users
              socket.emit('global-reaction', {
                reaction: reactionType,
                imgUrl: imgUrl,
                screenName: customerName
              });
              
              
              // Reset the auto-close timeout
              resetMenuTimeout();
            }
          });
          
          // Close menu when clicking outside
          document.addEventListener('click', function(e) {
            if (!e.target.closest('.global-reaction-container')) {
              closeGlobalReactionMenu();
            }
          });
          
          // Handle snooze/disable button click
          if (globalReactionSnoozeBtn) {
            globalReactionSnoozeBtn.addEventListener('click', function(e) {
              e.stopPropagation();
              
              if (isReactionsDisabled()) {
                // Enable reactions - remove both old and new keys
                localStorage.removeItem('mhv_reactions_disabled');
                localStorage.removeItem('mhv_reactions_snoozed_until');
              } else {
                // Disable reactions (persists until manually enabled)
                localStorage.setItem('mhv_reactions_disabled', 'true');
                localStorage.removeItem('mhv_reactions_snoozed_until'); // Clean up old key
              }
              
              updateSnoozeState();
            });
          }
        }
        
        // Handle incoming global reactions (display popping animation)
        socket.on('global-reaction', function(data) {
          // Only show if not snoozed
          if (!isReactionsSnoozed()) {
            createPoppingReaction(data.imgUrl);
          }
        });
        
        // Create the popping reaction animation
        function createPoppingReaction(imgUrl) {
          var el = document.createElement('div');
          el.className = 'popping-reaction';
          
          // Random horizontal position (10% to 90% of screen width)
          var xPos = 10 + Math.random() * 80;
          
          // Random variations for animation
          var duration = 1.8 + Math.random() * 0.8; // 1.8s to 2.6s
          var startRotation = -20 + Math.random() * 40; // -20 to 20 degrees
          var midRotation = -15 + Math.random() * 30;
          var endRotation = -10 + Math.random() * 20;
          var wobbleRotation = -25 + Math.random() * 50;
          var finalRotation = -30 + Math.random() * 60;
          
          // Start above the chat input area (~70px from bottom)
          el.style.cssText = 'left: ' + xPos + '%; bottom: 70px;' +
            '--pop-duration: ' + duration + 's;' +
            '--start-rotation: ' + startRotation + 'deg;' +
            '--mid-rotation: ' + midRotation + 'deg;' +
            '--end-rotation: ' + endRotation + 'deg;' +
            '--wobble-rotation: ' + wobbleRotation + 'deg;' +
            '--final-rotation: ' + finalRotation + 'deg;';
          
          var img = document.createElement('img');
          img.src = imgUrl;
          img.alt = 'Reaction';
          el.appendChild(img);
          
          document.body.appendChild(el);
          
          // Remove element after animation completes
          setTimeout(function() {
            if (el.parentNode) {
              el.parentNode.removeChild(el);
            }
          }, duration * 1000 + 100);
        }

        // Store socket reference for cleanup
        container.customChatSocket = socket;
        container.setAttribute('data-chat-widget-initialized', 'true');
        
        // ==========================================================================
        // COUNTDOWN BANNER
        // ==========================================================================
        
        var countdownBanner = chatContainer.querySelector('#chat-countdown-banner');
        var countdownCloseBtn = chatContainer.querySelector('#countdown-banner-close');
        var countdownInterval = null;
        var countdownWasRunning = false; // Track if countdown was actively running before hitting 0
        var countdownAutoOpenTriggered = false; // Prevent re-opening after admin closes chat

        if (countdownBanner) {
          // Fetch live status from schedule API instead of reading data-livetime attribute
          fetch(MHV.SERVER_URL + '/api/schedule/live-status')
            .then(function(r) { return r.json(); })
            .then(function(data) {
              if (data.livetime && data.livetime.length >= 16 && !data.ended) {
                startCountdown(data.livetime);
              } else {
                countdownBanner.classList.add('hidden');
              }
            })
            .catch(function() {
              // Fall back to data-livetime attribute if API fails
              var livetime = countdownBanner.dataset.livetime;
              if (livetime && livetime.length >= 16) {
                startCountdown(livetime);
              } else {
                countdownBanner.classList.add('hidden');
              }
            });

          // Close button handler - hides for this page load only
          if (countdownCloseBtn) {
            countdownCloseBtn.addEventListener('click', function() {
              countdownBanner.classList.add('hidden');
              if (countdownInterval) {
                clearInterval(countdownInterval);
              }
            });
          }
        }
        
        function startCountdown(liveTimeStr) {
          // Parse simplified livetime format (YYYY/MM/DD-HH:MM)
          var year = parseInt(liveTimeStr.slice(0, 4), 10);
          var month = parseInt(liveTimeStr.slice(5, 7), 10);
          var day = parseInt(liveTimeStr.slice(8, 10), 10);
          var hour = parseInt(liveTimeStr.slice(11, 13), 10);
          var minute = parseInt(liveTimeStr.slice(14, 16), 10);
          
          // Canonical DST-aware Eastern→UTC (LOG-99). Replaces a browser-timezone
          // getTimezoneOffset heuristic that could be wrong for non-Eastern viewers.
          var countdownDate = MHV.easternToUTC(year, month, day, hour, minute);
          
          function updateCountdownDisplay() {
            var now = new Date();
            var distance = countdownDate.getTime() - now.getTime();
            
            var daysEl = chatContainer.querySelector('#countdown-days');
            var hoursEl = chatContainer.querySelector('#countdown-hours');
            var minutesEl = chatContainer.querySelector('#countdown-minutes');
            var secondsEl = chatContainer.querySelector('#countdown-seconds');
            var infoEl = chatContainer.querySelector('#countdown-banner-info');
            var titleEl = countdownBanner.querySelector('.countdown-banner-title');
            
            if (distance < 0) {
              // Countdown ended - hide the countdown banner
              countdownBanner.classList.add('hidden');
              
              // Stop the interval
              if (countdownInterval) {
                clearInterval(countdownInterval);
              }
              
              // Auto-open chat ONLY if countdown was actively running and just hit 0
              // This prevents auto-opening if page loads after countdown already ended
              // or if admin manually closes the chat after it has opened
              if (countdownWasRunning && !countdownAutoOpenTriggered) {
                countdownAutoOpenTriggered = true; // Mark as triggered so it won't reopen
                if (chatContainer.classList.contains('chat-closed')) {
                  // Request server to open chat
                  socket.emit('countdown-ended');
                }
                // Show notification only on the actual countdown end moment
                addNotification('🔴 Auction is now live!', 'system');
              }
              
              return;
            }
            
            // Mark that the countdown is actively running (has time remaining)
            countdownWasRunning = true;
            
            var days = Math.floor(distance / (1000 * 60 * 60 * 24));
            var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            var seconds = Math.floor((distance % (1000 * 60)) / 1000);
            
            if (daysEl) daysEl.textContent = days < 10 ? '0' + days : days;
            if (hoursEl) hoursEl.textContent = hours < 10 ? '0' + hours : hours;
            if (minutesEl) minutesEl.textContent = minutes < 10 ? '0' + minutes : minutes;
            if (secondsEl) secondsEl.textContent = seconds < 10 ? '0' + seconds : seconds;
            
            // Show local time info
            if (infoEl) {
              var localShowDate = new Date(countdownDate.getTime());
              var dayOfWeek = localShowDate.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
              var dateStr = localShowDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
              var localHours = localShowDate.getHours();
              var localMinutes = localShowDate.getMinutes();
              var ampm = localHours >= 12 ? 'PM' : 'AM';
              localHours = localHours % 12;
              localHours = localHours ? localHours : 12;
              var minuteStr = localMinutes < 10 ? '0' + localMinutes : localMinutes;
              infoEl.textContent = dayOfWeek + ' ' + dateStr + ' @ ' + localHours + ':' + minuteStr + ' ' + ampm;
            }
          }
          
          // Initial update
          updateCountdownDisplay();
          
          // Update every second
          countdownInterval = setInterval(updateCountdownDisplay, 1000);
        }

      } catch (error) {
        console.error('❌ Custom Chat initialization error:', error);
        if (loadingEl) {
          loadingEl.innerHTML = '<p>Failed to initialize chat</p>';
        }
      }
    });
  }

  // Initialize all custom chat widgets on the page
  function initAllCustomChats() {
    var containers = document.querySelectorAll('.chat-widget-wrapper.custom-chat');
    containers.forEach(function(container) {
      initCustomChat(container);
    });
  }

  // Start initialization
  function startInitialization() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        waitForSocketIO(initAllCustomChats);
      });
    } else {
      waitForSocketIO(initAllCustomChats);
    }
  }

  startInitialization();

  // Expose functions globally for external control
  // =========================================================================
  // GIF MESSAGE RENDERING
  // =========================================================================
  // SEC-85: only render a [gif:URL] as an <img> if the URL is actually a GIPHY
  // host. The SEND path already requires giphy.com, but the RENDER path did not —
  // so a crafted "[gif:https://evil.com/x.gif]" chat line would load an arbitrary
  // remote image (tracking pixel / offensive content) for every viewer. Restrict
  // rendering to giphy hosts; anything else falls through to escaped plain text.
  function isGiphyGifUrl(u) {
    try {
      var h = new URL(u).hostname.toLowerCase();
      return h === 'giphy.com' || h.endsWith('.giphy.com');
    } catch (e) { return false; }
  }
  function renderChatMessageContent(message) {
    // Check for GIF message format: [gif:URL]
    var gifMatch = message.match(/^\[gif:(https:\/\/[^\]]+)\]$/);
    if (gifMatch && isGiphyGifUrl(gifMatch[1])) {
      return '<img class="chat-gif-message" src="' + MHV.escapeAttr(gifMatch[1]) + '" alt="GIF" loading="lazy" onload="this.closest(\'.custom-chat-messages\')?.scrollTo(0, this.closest(\'.custom-chat-messages\').scrollHeight)">' +
        '<span class="chat-gif-hidden-label">GIF hidden</span>';
    }
    return MHV.escapeHtml(message);
  }

  // Apply hide-gifs preference on load
  if (localStorage.getItem('mhv_hide_gifs') === 'true') {
    document.documentElement.classList.add('hide-chat-gifs');
  }

  window.toggleHideGifs = function(hide) {
    localStorage.setItem('mhv_hide_gifs', hide ? 'true' : 'false');
    document.documentElement.classList.toggle('hide-chat-gifs', hide);
  };

  // =========================================================================
  // GIF PICKER PANEL
  // =========================================================================
  (function() {
    var gifPanel = null;
    var gifSearchTimeout = null;
    var SERVER = (window.MHV && MHV.SERVER_URL) || '';

    // Rate limit: 1 GIF per 60 seconds
    var lastGifSentAt = 0;
    var GIF_COOLDOWN = 60000;
    var countdownInterval = null;

    function getCooldownRemaining() {
      if (!lastGifSentAt) return 0;
      var remaining = GIF_COOLDOWN - (Date.now() - lastGifSentAt);
      return remaining > 0 ? remaining : 0;
    }

    function isOnCooldown() {
      return getCooldownRemaining() > 0;
    }

    function formatCountdown(ms) {
      return Math.ceil(ms / 1000) + 's';
    }

    // Show countdown in the grid area
    function showCooldownInGrid() {
      var grid = document.getElementById('chat-gif-grid');
      if (!grid) return;
      clearInterval(countdownInterval);

      function renderCountdown() {
        var remaining = getCooldownRemaining();
        if (remaining <= 0) {
          clearInterval(countdownInterval);
          countdownInterval = null;
          // Cooldown ended — re-enable search and load trending
          if (gifPanel && gifPanel.classList.contains('visible')) {
            var searchInput = gifPanel.querySelector('#chat-gif-search');
            if (searchInput) { searchInput.disabled = false; searchInput.classList.remove('cooldown-disabled'); }
            loadGifs('trending');
          }
          return;
        }
        var secs = Math.ceil(remaining / 1000);
        var padded = (secs < 10 ? '0' : '') + secs + 's';
        grid.innerHTML =
          '<div class="chat-gif-countdown">' +
            '<div class="chat-gif-countdown-text">You can send your next GIF in</div>' +
            '<div class="chat-gif-countdown-seconds">' + padded + '</div>' +
          '</div>';
        // Disable search during cooldown
        var searchInput = gifPanel && gifPanel.querySelector('#chat-gif-search');
        if (searchInput) { searchInput.disabled = true; searchInput.classList.add('cooldown-disabled'); }
      }

      renderCountdown();
      countdownInterval = setInterval(renderCountdown, 1000);
    }

    // Update the small cooldown note in the attribution bar (static label, no timer)
    function updateCooldownNote() {
      // no-op — label is always "1 GIF/min"
    }

    function createGifPanel() {
      if (gifPanel) return gifPanel;
      gifPanel = document.createElement('div');
      gifPanel.id = 'chat-gif-panel';
      gifPanel.className = 'chat-gif-panel';
      gifPanel.innerHTML =
        '<div class="chat-gif-panel-header">' +
          '<input type="text" class="chat-gif-search" id="chat-gif-search" placeholder="Search GIFs..." autocomplete="off">' +
          '<button class="chat-gif-close" onclick="closeChatGifPanel()">\u00D7</button>' +
        '</div>' +
        '<div class="chat-gif-grid" id="chat-gif-grid">' +
          '<div class="chat-gif-loading">Loading...</div>' +
        '</div>' +
        '<div class="chat-gif-attribution">' +
          '<img src="https://cdn.shopify.com/s/files/1/0073/9101/9067/files/GIPHY-ATTRIBUTION-MARK.gif?v=1775542374" alt="Powered by GIPHY" class="chat-gif-giphy-mark">' +
          '<span class="chat-gif-cooldown-note" id="chat-gif-cooldown-note">Limit: 1 GIF/min</span>' +
          '<label class="chat-gif-hide-toggle">' +
            '<span>Hide GIFs</span>' +
            '<input type="checkbox" id="chat-gif-hide-checkbox" ' + (localStorage.getItem('mhv_hide_gifs') === 'true' ? 'checked' : '') + ' onchange="toggleHideGifs(this.checked)">' +
            '<span class="chat-gif-hide-slider"></span>' +
          '</label>' +
        '</div>';

      // Insert inside the chat container, positioned above the input area
      var chatContainer = document.querySelector('.custom-chat-container');
      if (chatContainer) {
        chatContainer.style.position = 'relative';
        chatContainer.appendChild(gifPanel);
      }

      // Resize panel when mobile keyboard opens/closes
      if (window.visualViewport) {
        var baseHeight = 280;
        window.visualViewport.addEventListener('resize', function() {
          if (!gifPanel.classList.contains('visible')) return;
          var available = window.visualViewport.height;
          var panelMax = Math.min(baseHeight, available * 0.55);
          gifPanel.style.height = Math.max(180, panelMax) + 'px';
        });
        var origClose = window.closeChatGifPanel;
        window.closeChatGifPanel = function() {
          gifPanel.style.height = '';
          origClose();
        };
      }

      // Search handler with 2-second debounce + spinner
      var searchInput = gifPanel.querySelector('#chat-gif-search');
      searchInput.addEventListener('input', function() {
        clearTimeout(gifSearchTimeout);
        var q = this.value.trim();

        // Immediately show spinner while user types
        var grid = document.getElementById('chat-gif-grid');
        if (grid) {
          grid.innerHTML = '<div class="chat-gif-loading"><div class="chat-gif-search-spinner"></div></div>';
        }

        gifSearchTimeout = setTimeout(function() {
          lastSearchQuery = q;
          lastSearchTime = Date.now();
          if (isOnCooldown()) {
            showCooldownInGrid();
          } else if (q) {
            loadGifs('search', q);
          } else {
            loadGifs('trending');
          }
        }, 2000);
      });

      return gifPanel;
    }

    function loadGifs(mode, query) {
      var grid = document.getElementById('chat-gif-grid');
      if (!grid) return;
      grid.innerHTML = '<div class="chat-gif-loading"><div class="chat-gif-search-spinner"></div></div>';

      var url = mode === 'search'
        ? SERVER + '/api/giphy/search?q=' + encodeURIComponent(query) + '&limit=20'
        : SERVER + '/api/giphy/trending?limit=20';

      var fetchFn = window.mhvFetch || fetch;
      fetchFn(url)
        .then(function(r) {
          if (r.status === 429) {
            // API rate limit hit — server auto-disables via socket
            grid.innerHTML = '<div class="chat-gif-loading">GIF service temporarily unavailable. Try again later!</div>';
            return null;
          }
          return r.json();
        })
        .then(function(data) {
          if (!data) return;
          if (!data.gifs || data.gifs.length === 0) {
            grid.innerHTML = '<div class="chat-gif-loading">No GIFs found</div>';
            return;
          }
          grid.innerHTML = data.gifs.map(function(gif) {
            return '<img class="chat-gif-option" src="' + MHV.escapeAttr(gif.preview || gif.url) + '" ' +
              'data-full-url="' + MHV.escapeAttr(gif.url) + '" ' +
              'alt="' + MHV.escapeAttr(gif.title) + '" loading="lazy">';
          }).join('');

          grid.querySelectorAll('.chat-gif-option').forEach(function(img) {
            img.addEventListener('click', function() {
              sendGifMessage(this.dataset.fullUrl);
            });
          });
        })
        .catch(function() {
          grid.innerHTML = '<div class="chat-gif-loading">Failed to load GIFs</div>';
        });
    }

    window.showGifToast = function(msg) {
      var existing = document.getElementById('chat-gif-toast');
      if (existing) existing.remove();
      var toast = document.createElement('div');
      toast.id = 'chat-gif-toast';
      toast.className = 'chat-gif-toast';
      toast.textContent = msg;
      var container = document.querySelector('.custom-chat-container');
      if (container) container.appendChild(toast);
      setTimeout(function() { toast.classList.add('visible'); }, 10);
      setTimeout(function() { toast.classList.remove('visible'); setTimeout(function() { toast.remove(); }, 300); }, 2500);
    }

    function sendGifMessage(gifUrl) {
      if (!gifUrl || !gifUrl.includes('giphy.com')) return;

      // Rate limit: 1 per minute
      if (isOnCooldown()) {
        showGifToast('Wait ' + formatCountdown(getCooldownRemaining()) + ' before sending another GIF.');
        return;
      }
      lastGifSentAt = Date.now();

      var container = document.querySelector('.custom-chat-container');
      if (!container) return;
      var socket = document.querySelector('.chat-widget-wrapper.custom-chat')?.customChatSocket;
      if (!socket) return;

      var customerId = container.dataset.customerId;
      var customerName = container.dataset.customerName;
      var customerFirstName = container.dataset.customerFirstName;
      var customerBadge = container.dataset.customerBadge;
      var customerBidderNumber = container.dataset.customerBidderNumber;
      var customerImage = container.dataset.customerImage;

      socket.emit('chat-message', {
        message: '[gif:' + gifUrl + ']',
        screenName: customerName,
        firstName: customerFirstName,
        badge: customerBadge,
        bidderNumber: customerBidderNumber,
        profileImage: customerImage,
        customerId: customerId,
        timestamp: Date.now()
      });

      closeChatGifPanel();
    }

    var lastSearchQuery = '';
    var lastSearchTime = 0;
    var SEARCH_PERSIST_MS = 60000;

    window.toggleChatGifPanel = function() {
      var panel = createGifPanel();
      if (panel.classList.contains('visible')) {
        panel.classList.remove('visible');
        clearInterval(countdownInterval);
        countdownInterval = null;
      } else {
        panel.classList.add('visible');
        updateCooldownNote();
        var searchInput = panel.querySelector('#chat-gif-search');
        var now = Date.now();

        // If on cooldown, show countdown instead of making API calls
        if (isOnCooldown()) {
          if (searchInput) searchInput.value = '';
          lastSearchQuery = '';
          showCooldownInGrid();
        } else if (lastSearchQuery && (now - lastSearchTime) < SEARCH_PERSIST_MS) {
          if (searchInput) searchInput.value = lastSearchQuery;
          loadGifs('search', lastSearchQuery);
        } else {
          lastSearchQuery = '';
          if (searchInput) searchInput.value = '';
          loadGifs('trending');
        }
      }
    };

    window.closeChatGifPanel = function() {
      if (gifPanel) gifPanel.classList.remove('visible');
      clearInterval(countdownInterval);
      countdownInterval = null;
    };

    // GIF button wired via onclick in HTML
  })();

  window.CustomChatWidget = {
    init: function() {
      waitForSocketIO(initAllCustomChats);
    },
    getSocket: function() {
      var container = document.querySelector('.chat-widget-wrapper.custom-chat');
      return container ? container.customChatSocket : null;
    }
  };
})();
